import { showConfirmation } from '../lib/confirmation';
import { invoiceDueTermOptions, clientInvoiceDueTerm, invoiceDueDaysForTerm } from '../utils/invoiceDueTerms';
import ClientActivityLog from '../components/ClientActivityLog';
import useRecordSelection from '../lib/useRecordSelection';
import { formatDate as format, normalizeSearch, formatName, formatEmail, formatTime } from '../lib/format';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import FieldLabel from '../components/ui/FieldLabel';
import UiInput from '../components/ui/Input';
import UiCheckbox from '../components/ui/Checkbox';
import SearchInput from '../components/ui/SearchInput';
import Button from '../components/ui/Button';
import SitePortalAccess from '../components/SitePortalAccess';
import EmailRecipientPills from '../components/EmailRecipientPills';
import { showLinkDownloadProgress } from '../utils/downloadProgress';
import { Fragment, useState, useEffect, useRef, useMemo } from 'react';
import Input from '../components/Input';
import Dropdown from '../components/Dropdown';
import { createEmptyLabourRates, getDropdownValue, labourRateBandKeys, normalizeLabourRates, normalizeTimesheetEmailFrequency, postWithAuth, randomString } from '../common';
import { equipmentOptions, googleApiKey, workerTypes } from '../constants';
import { useSelector } from 'react-redux';
import Header from "../components/Header";
import AutoComplete from "react-google-autocomplete";
import Sidebar from '../components/Sidebar';
import Section from '../components/Section';
import { toast } from 'react-toastify';
import useTableSort from '../components/useTableSort';
import RateTable from '../components/RatesTable';
import EmployeeRatesTable from '../components/EmployeeRatesTable';
import MyDropzone from '../components/MyDropzone';
import MyCalendar from '../components/Calendar';
import Checkbox from '../components/Checkbox';
import { PlusIcon, EditIcon, CopyIcon, ArchiveIcon, TrashIcon, ReceiptIcon, MailIcon, CheckIcon } from '../components/NavIcons';
import { useSearchParams } from 'react-router-dom';
import CompactSuccessModal from '../components/CompactSuccessModal';
import ModalComponent from '../components/Modal';
import LoadingPopup from '../components/LoadingPopup';
import Documentation from './Documentation';
import { uploadFileToStorage } from '../storageUpload';
import { createEmptyEmployeeRates, normalizeEmployeeRates } from '../utils/workerRates';
const formatCompanyName = (name) =>
    String(name || '').toLowerCase().replace(/(^|\s)(\S)/g, (match) => match.toUpperCase())

const whsDocumentationOptions = [
    'Safe Work Method Statement',
    'Job Safety Analysis',
    'Incident Report',
    'Daily Vehicle Checklist',
    'Daily TGS Checklist',
].map((type) => ({ label: type, value: type }))

const whsSwmsSubcategoryOptions = [
    'General Labour',
    'Telehandler',
    'Traffic Management',
    'Elevated Work Platform (Over 11m)',
    'Demolition',
].map((type) => ({ label: type, value: type }))

const getDocumentFileName = (picture) => {
    const rawUrl = typeof picture === 'string' ? picture : picture?.url
    if (!rawUrl) return 'Document'
    try {
        const withoutQuery = rawUrl.split('?')[0]
        const lastSegment = withoutQuery.split('/').pop() || 'Document'
        return decodeURIComponent(lastSegment)
    } catch (e) {
        return 'Document'
    }
}

const getDocumentDisplayName = (row) => {
    const explicitName = String(row?.name || row?.formData?.name || '').trim()
    if (explicitName) return explicitName
    return getDocumentFileName(row?.picture)
}

// Site forms saved by older mobile builds are stored as
// `{ form: true, formData: entry, note: category, picture: null }`; web saves keep
// the entry at the top level with `form` as the form object.
const getSiteFormData = (row) => (row?.form && typeof row.form === 'object' ? row.form : row?.formData?.form) || null
const getSiteFormCategory = (row) => row?.category || row?.formData?.category || row?.note || ''
const getSiteFormType = (row) => row?.type || row?.formData?.type || ''
// Only a Safe Work Method Statement in the web's layout (list fields as arrays) can
// open in the web form editor. Other forms, such as those filled in on mobile, open
// as their PDF, so saving from the editor can never overwrite what was entered.
const isWebSwmsForm = (row) => getSiteFormType(row) === 'Safe Work Method Statement'
    && Array.isArray(getSiteFormData(row)?.hazards)

const previewFrameStyle = {
    border: '1px solid #e6ebf2',
    borderRadius: '8px',
    padding: '12px',
    backgroundColor: '#f8fafc',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '120px',
}

const normalizeAbn = (value) => String(value || '').trim().replace(/\s+/g, '')
const isDraftSiteId = (id) => String(id || '').startsWith('draft-site-')
// saveClientInfo stores only each site's id and name; full site records (rates,
// documents) are saved through saveSiteInfo and must not be round-tripped here.
const siteSummaries = (sites = []) => sites
    .filter(site => site?.id && !isDraftSiteId(site.id))
    .map(site => ({ id: site.id, name: site.name || '' }))
const buildLegacyLabourRateFields = (source = {}) => Object.fromEntries(
    labourRateBandKeys.map((band) => [band, source?.[band] || []])
)


const ClientProfile = () => {

    const userInfo = useSelector((state) => state.user.user)
    const activeOrg = useSelector((state) => state.user.activeOrg)
    const isStaff = userInfo?.isStaff

    const [clients, setClients] = useState([])
    const [searchParams, setSearchParams] = useSearchParams()
    const clientIdFromUrl = searchParams.get('clientId') || ''
    const siteIdFromUrl = searchParams.get('siteId') || ''
    const openNewClientFromUrl = searchParams.get('newClient') === '1'
    const [viewingClient, setViewingClient] = useRecordSelection('fetchClients', activeOrg?.id, record => setClients(rows => rows.map(row => row.id === record.id ? record : row)))
    const [clientSearch, setClientSearch] = useState('')
    const clientListScrollPosition = useRef(null)

    const [loading, setLoading] = useState(false)
    const [successMessage, setSuccessMessage] = useState(null)
    const [editing, setEditing] = useState(false)
    const [archivingClient, setArchivingClient] = useState(false)
    const [deletingClient, setDeletingClient] = useState(false)
    const [showAddContactModal, setShowAddContactModal] = useState(false)
    const [additionalContacts, setAdditionalContacts] = useState([])
    const [primaryContactId, setPrimaryContactId] = useState('')
    const [primaryContactNo, setPrimaryContactNo] = useState('')
    const [contactEditingId, setContactEditingId] = useState('')
    const [contactManagerMode, setContactManagerMode] = useState(false)
    const [contactPendingDelete, setContactPendingDelete] = useState(null)
    const [contactFirstName, setContactFirstName] = useState('')
    const [contactLastName, setContactLastName] = useState('')
    const [contactEmail, setContactEmail] = useState('')
    const [contactNumber, setContactNumber] = useState('')
    const [companyName, setCompanyName] = useState('')
    const [businessType, setBusinessType] = useState('company')
    const [abn, setAbn] = useState('')
    const [address, setAddress] = useState('')
    const [coordinates, setCoordinates] = useState([])
    const [contactNo, setContactNo] = useState('')

    const [rateDayEquipmentCompany, setRateDayEquipmentCompany] = useState([])
    const [companyLabourRates, setCompanyLabourRates] = useState(createEmptyLabourRates(workerTypes))

    const [firstName, setFirstName] = useState('')
    const [lastName, setLastName] = useState('')
    const [email, setEmail] = useState('')
    const [invoiceEmail, setInvoiceEmail] = useState('')
    const [invoiceCcEmails, setInvoiceCcEmails] = useState('')
    const [invoiceBccEmails, setInvoiceBccEmails] = useState('')
    const [invoiceDueDays, setInvoiceDueDays] = useState('7')
    const [invoiceDueTerm, setInvoiceDueTerm] = useState('days7')
    const [invoiceDueTermsSupported, setInvoiceDueTermsSupported] = useState(false)
    const [sites, setSites] = useState([])
    const [invoices, setInvoices] = useState([])
    const [expandedClientInvoiceId, setExpandedClientInvoiceId] = useState('')
    const [clientInvoicePage, setClientInvoicePage] = useState(1)

    const [viewingSite, setViewingSite] = useState(false)
    const [siteImagePreview, setSiteImagePreview] = useState(null)
    const [editingSite, setEditingSite] = useState(false)
    const [workers, setWorkers] = useState([])

    const [siteName, setSiteName] = useState('')
    const [siteAddress, setSiteAddress] = useState('')
    const [siteCoordinates, setSiteCoordinates] = useState('')
    const [siteArchived, setSiteArchived] = useState(false)

    const [clientAccessContacts, setClientAccessContacts] = useState([])
    const [managingPortalAccess, setManagingPortalAccess] = useState(false)
    const [managerFirstName, setManagerFirstName] = useState('')
    const [managerLastName, setManagerLastName] = useState('')
    const [managerEmail, setManagerEmail] = useState('')
    const [managerContactNo, setManagerContactNo] = useState('')
    const siteManagerPortalContact = { firstName: managerFirstName, lastName: managerLastName, mobile: managerContactNo, email: managerEmail }
    const hasSiteManagerContact = Object.values(siteManagerPortalContact).some(value => String(value || '').trim())
    const isSiteManagerContact = contact => {
        const email = String(contact.email || '').trim().toLowerCase()
        const mobile = String(contact.mobile || '').replace(/\D/g, '')
        return (email && email === String(managerEmail || '').trim().toLowerCase()) || (mobile && mobile === String(managerContactNo || '').replace(/\D/g, ''))
    }

    const [sendTimesheetsByEmail, setSendTimesheetsByEmail] = useState(false)
    const [timesheetEmailFrequency, setTimesheetEmailFrequency] = useState('')
    const [timesheetEmailRecipients, setTimesheetEmailRecipients] = useState('')

    const [rateDayEquipment, setRateDayEquipment] = useState([])
    const [siteLabourRates, setSiteLabourRates] = useState(createEmptyLabourRates(workerTypes))
    const [applyWorkerRatesToSite, setApplyWorkerRatesToSite] = useState(false)
    const [siteEmployeeRates, setSiteEmployeeRates] = useState(createEmptyEmployeeRates())

    const [addPicture, setAddPicture] = useState(false)
    const [addPictureIndex, setAddPictureIndex] = useState('')
    const [picture, setPicture] = useState('')
    const [pictureName, setPictureName] = useState('')
    const [pictureDate, setPictureDate] = useState('')
    const [pictureNote, setPictureNote] = useState('')
    const [pictureIncludeInNewAllocation, setPictureIncludeInNewAllocation] = useState(false)
    const [showSiteDocumentCancelConfirm, setShowSiteDocumentCancelConfirm] = useState(false)
    const [pictures, setPictures] = useState([])
    const [whsDocuments, setWhsDocuments] = useState([])
    const [showAddWhsDocumentModal, setShowAddWhsDocumentModal] = useState(false)
    const [showWhsFormModal, setShowWhsFormModal] = useState(false)
    const [whsDocumentType, setWhsDocumentType] = useState('')
    const [whsDocumentSubcategory, setWhsDocumentSubcategory] = useState('')
    const [whsFormInitialData, setWhsFormInitialData] = useState(null)
    const [whsFormEditingId, setWhsFormEditingId] = useState(null)
    const [whsSiteDocumentIndex, setWhsSiteDocumentIndex] = useState(null)
    const [whsFormReadOnly, setWhsFormReadOnly] = useState(false)
    const [workerUploads, setWorkerUploads] = useState([])
    const [siteImagePage, setSiteImagePage] = useState(1)
    const siteImagePageCount = Math.max(1, Math.ceil(workerUploads.length / 5))
    const currentSiteImagePage = Math.min(siteImagePage, siteImagePageCount)
    useEffect(() => { setSiteImagePage(1) }, [viewingSite?.id])

    const [triggerSaveInformation, setTriggerSaveInformation] = useState('')
    const [abnVerified, setAbnVerified] = useState(false)
    const [abnVerifying, setAbnVerifying] = useState(false)
    const [customEquipmentTypes, setCustomEquipmentTypes] = useState([])
    const [removedEquipmentTypes, setRemovedEquipmentTypes] = useState([])
    const [renamedEquipmentTypes, setRenamedEquipmentTypes] = useState({})
    const [showAddEquipmentTypeModal, setShowAddEquipmentTypeModal] = useState(false)
    const [newEquipmentTypeName, setNewEquipmentTypeName] = useState('')
    const [equipmentTypePendingDelete, setEquipmentTypePendingDelete] = useState(null)
    const [equipmentTypeEditing, setEquipmentTypeEditing] = useState(null)
    const [showInvoiceEmailModal, setShowInvoiceEmailModal] = useState(false)
    const [newInvoiceEmail, setNewInvoiceEmail] = useState('')
    const [invoiceEmailEditingIndex, setInvoiceEmailEditingIndex] = useState(null)
    const [invoiceEmailPendingDelete, setInvoiceEmailPendingDelete] = useState(null)
    const [invoiceEmailSaveSuccess, setInvoiceEmailSaveSuccess] = useState(false)

    const businessTypeOptions = [
        { label: 'Company', value: 'company' },
        { label: 'Individual', value: 'individual' },
    ]
    const timesheetEmailFrequencyOptions = [
        { label: 'Everyday', value: 'everyday' },
        { label: 'Everyweek', value: 'everyweek' },
        { label: 'On Completion', value: 'oncompletion' },
    ]
    useEffect(() => {
        if (activeOrg) {
            fetchClients()
            fetchWorkers()
        }
    }, [activeOrg])

    useEffect(() => {
        setInvoices([])
        if (viewingClient?.id && activeOrg?.id) fetchInvoices()
    }, [viewingClient?.id, activeOrg?.id])

    const fetchInvoices = async () => {
        try {
            const resp = await postWithAuth('fetchInvoices', { orgId: activeOrg.id })
            if (resp?.err) {
                toast.error(resp.err)
                return
            }
            setInvoices(Array.isArray(resp?.data) ? resp.data.filter(row => row.client) : [])
        } catch (e) {
            toast.error(e.message)
        }
    }

    const openClientPanel = (client) => {
        clientListScrollPosition.current = window.scrollY
        if (client?.id) {
            const next = new URLSearchParams(searchParams)
            next.set('clientId', client.id)
            next.delete('siteId')
            setSearchParams(next)
        }
        // Never show (or save) one client's sites under another client.
        if (client?.id !== viewingClient?.id) {
            setSites([])
            setViewingSite(false)
            setEditingSite(false)
        }
        setViewingClient(client)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const closeClientPanel = () => {
        if (clientIdFromUrl || siteIdFromUrl) {
            const next = new URLSearchParams(searchParams)
            next.delete('clientId'); next.delete('siteId')
            setSearchParams(next)
        }
        setEditing(false)
        setViewingClient()
        setSites([])
        setViewingSite(false)
        setEditingSite(false)
        if (clientListScrollPosition.current !== null) {
            const savedScrollPosition = clientListScrollPosition.current
            clientListScrollPosition.current = null
            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    window.scrollTo({ top: savedScrollPosition, behavior: 'auto' })
                })
            })
        }
    }

    const handleArchiveClient = async (archived) => {
        if (!viewingClient?.id || !activeOrg?.id || archivingClient) return
        if (!window.confirm(`Are you sure you want to ${archived ? 'archive' : 'unarchive'} this client?`)) return
        try {
            setArchivingClient(true)
            const resp = await postWithAuth('archiveClient', { id: viewingClient.id, orgId: activeOrg.id, archived })
            if (resp?.err) throw new Error(resp.err)
            await fetchClients()
            setSuccessMessage({
                title: archived ? 'Client archived' : 'Client unarchived',
                message: archived ? 'Client hidden from normal lists.' : 'Client restored to normal lists.',
            })
            if (archived) {
                closeClientPanel()
            } else {
                setViewingClient(current => current ? { ...current, archived: false } : current)
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setArchivingClient(false)
        }
    }

    const handleDeleteClient = async () => {
        if (!viewingClient?.id || !activeOrg?.id || deletingClient) return
        if (!window.confirm('Delete this client? This only works when there are no linked sites, allocations, timesheets, or invoices.')) return
        try {
            setDeletingClient(true)
            const resp = await postWithAuth('deleteClient', { id: viewingClient.id, orgId: activeOrg.id })
            if (resp?.err) throw new Error(resp.err)
            await fetchClients()
            setSuccessMessage({
                title: 'Client deleted',
                message: 'Client removed successfully.',
            })
            closeClientPanel()
        } catch (e) {
            toast.error(e.message)
        } finally {
            setDeletingClient(false)
        }
    }

    const openAddContactModal = () => {
        setContactManagerMode(false)
        setContactEditingId('')
        setContactFirstName('')
        setContactLastName('')
        setContactEmail('')
        setContactNumber('')
        setShowAddContactModal(true)
    }

    const openEditContactModal = () => {
        setContactManagerMode(true)
        setContactEditingId('')
        setContactFirstName('')
        setContactLastName('')
        setContactEmail('')
        setContactNumber('')
        setShowAddContactModal(true)
    }

    const closeAddContactModal = () => {
        setShowAddContactModal(false)
        setContactFirstName('')
        setContactLastName('')
        setContactEmail('')
        setContactNumber('')
        setContactEditingId('')
        setContactManagerMode(false)
        setContactPendingDelete(null)
    }

    const handleAddContact = async () => {
        const nextFirstName = contactFirstName.trim()
        const nextLastName = contactLastName.trim()
        const nextEmail = contactEmail.trim().toLowerCase()

        if (!nextFirstName || !nextLastName || !nextEmail) {
            toast.error('Enter the contact first name, last name and email')
            return
        }

        // Adding a contact is never an edit of the primary contact, even when no
        // primary is set (both ids would then be '').
        const editingPrimaryContact = !!contactEditingId && contactEditingId === primaryContactId
        const nextContact = {
            id: contactEditingId || `contact-${Date.now()}`,
            firstName: nextFirstName,
            lastName: nextLastName,
            email: nextEmail,
            contactNo: contactNumber.trim(),
            isPrimary: editingPrimaryContact,
        }
        const nextContacts = contactEditingId
            ? additionalContacts.map((contact) => contact.id === contactEditingId ? nextContact : contact)
            : [...additionalContacts, nextContact]

        // While the client form is being edited the contact list is saved by the
        // main Save (like contact deletes); saving here would also persist every
        // other unsaved edit on the form.
        if (viewingClient?.id && !editing) {
            try {
                setLoading(true)
                if (['receipt', 'endOfMonth30'].includes(invoiceDueTerm) && !invoiceDueTermsSupported) {
                    throw new Error('This invoice due period requires the server update before it can be saved.')
                }
                const resp = await postWithAuth('saveClientInfo', {
                    id: viewingClient.id,
                    name: companyName,
                    businessType,
                    abn: businessType === 'company' ? normalizeAbn(abn) : '',
                    address,
                    coordinates,
                    contactNo,
                    rateDayEquipment: rateDayEquipmentCompany,
                    customEquipmentTypes,
                    removedEquipmentTypes,
                    renamedEquipmentTypes,
                    labourRates: companyLabourRates,
                    primaryFirstName: editingPrimaryContact ? nextFirstName : firstName,
                    primaryLastName: editingPrimaryContact ? nextLastName : lastName,
                    primaryContactNo: editingPrimaryContact ? contactNumber.trim() : primaryContactNo,
                    primaryEmail: editingPrimaryContact ? nextEmail : email,
                    contacts: nextContacts,
                    invoiceEmail,
                    invoiceCcEmails,
                    invoiceBccEmails,
                    invoiceDueDays,
                    invoiceDueTerm,
                    sites: siteSummaries(sites),
                    orgId: activeOrg.id,
                })
                if (resp?.err) throw new Error(resp.err)
                const refreshedClients = await fetchClients()
                const refreshedClient = refreshedClients?.find((client) => client.id === viewingClient.id)
                const savedClient = {
                    ...(refreshedClient || viewingClient),
                    contacts: nextContacts,
                }
                setClients((currentClients) => currentClients.map((client) => (
                    client.id === viewingClient.id ? savedClient : client
                )))
                setViewingClient(savedClient)
            } catch (e) {
                toast.error(e.message)
                setLoading(false)
                return
            }
        }

        setAdditionalContacts(nextContacts)
        if (editingPrimaryContact) {
            setFirstName(nextContact.firstName)
            setLastName(nextContact.lastName)
            setPrimaryContactNo(nextContact.contactNo)
            setEmail(nextContact.email)
        }
        setLoading(false)
        closeAddContactModal()
        setSuccessMessage({
            title: contactEditingId ? 'Contact updated' : 'Contact added',
            message: `${nextFirstName} ${nextLastName} was ${contactEditingId ? 'updated' : 'added'} successfully.`,
        })
    }

    const selectPrimaryContact = (contact) => {
        setPrimaryContactId(contact.id)
        setAdditionalContacts((contacts) => contacts.map((item) => ({
            ...item,
            isPrimary: item.id === contact.id,
        })))
        setFirstName(contact.firstName || '')
        setLastName(contact.lastName || '')
        setPrimaryContactNo(contact.contactNo || '')
        setEmail(contact.email || '')
    }

    const openSitePanel = (site) => {
        createdSiteId.current = ''
        if (site?.id && !isDraftSiteId(site.id)) {
            const next = new URLSearchParams(searchParams)
            next.set('siteId', site.id)
            setSearchParams(next)
        }
        setViewingSite(site)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const openNewSitePanel = () => {
        createdSiteId.current = ''
        setViewingSite({
            rateDayEquipment: rateDayEquipmentCompany,
            labourRates: companyLabourRates,
            sendTimesheetsByEmail: false,
            timesheetEmailFrequency: '',
            timesheetEmailRecipients: '',
        })
        setEditingSite(true)
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }

    const closeSitePanel = () => {
        if (siteIdFromUrl) {
            const next = new URLSearchParams(searchParams)
            next.delete('siteId')
            setSearchParams(next)
        }
        setViewingSite(false)
        setEditingSite(false)
    }

    // Open from the URL (deep link / forward navigation).
    useEffect(() => {
        if (!openNewClientFromUrl) return
        createdClientIdRef.current = ''
        savedDraftSiteIdsRef.current = new Set()
        setSites([])
        setViewingSite(false)
        setViewingClient({})
        setEditing(true)
    }, [openNewClientFromUrl])

    useEffect(() => {
        if (!clientIdFromUrl || clients.length === 0) return
        if (viewingClient?.id === clientIdFromUrl) return
        const match = clients.find(c => c.id === clientIdFromUrl)
        if (match) {
            // Back/Forward straight from one client to another: never show (or save)
            // the previous client's sites under this one.
            if (viewingClient?.id && viewingClient.id !== match.id) {
                setSites([])
                setViewingSite(false)
                setEditingSite(false)
            }
            setViewingClient(match)
        }
    }, [clientIdFromUrl, clients, viewingClient])

    useEffect(() => {
        if (!siteIdFromUrl || sites.length === 0) return
        if (viewingSite?.id === siteIdFromUrl) return
        const match = sites.find(site => site.id === siteIdFromUrl)
        if (match) setViewingSite(match)
    }, [siteIdFromUrl, sites, viewingSite])

    // Close when the param disappears (browser Back).
    useEffect(() => {
        if (!clientIdFromUrl && viewingClient?.id) {
            setEditing(false)
            setViewingClient()
            setSites([])
            setViewingSite(false)
            setEditingSite(false)
        }
    }, [clientIdFromUrl])

    useEffect(() => {
        if (!siteIdFromUrl && viewingSite?.id) {
            setEditingSite(false)
            setViewingSite(false)
        }
    }, [siteIdFromUrl])

    useEffect(() => {
        if (triggerSaveInformation) {
            const saveAction = triggerSaveInformation
            setTriggerSaveInformation(false)
            handleSaveSiteInfo({ keepSiteOpen: true }).then((saved) => {
                if (saved) {
                    setSuccessMessage({
                        title: saveAction === 'updated' ? 'Site document updated' : 'Site document saved',
                        message: saveAction === 'updated'
                            ? 'Site document updated successfully.'
                            : 'Site document saved successfully.',
                    })
                }
            })
        }
    }, [triggerSaveInformation])

    const fetchClients = async () => {

        try {

            const resp = await postWithAuth('fetchClients', { orgId: activeOrg.id, listOnly: true })
            setInvoiceDueTermsSupported(resp?.capabilities?.invoiceDueTerms === true)

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                const mergedClients = resp.data.map((client) => {
                    const currentClient = clients.find((item) => item.id === client.id)
                    const returnedContacts = Array.isArray(client.contacts) ? client.contacts : []
                    const currentContacts = Array.isArray(currentClient?.contacts) ? currentClient.contacts : []
                    return returnedContacts.length === 0 && currentContacts.length > 0
                        ? { ...client, contacts: currentContacts }
                        : client
                })
                setClients(mergedClients)
                return mergedClients
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    // The client whose page is open; a slow reply for a client the admin has since
    // left must not replace the open client's sites.
    const openClientIdRef = useRef(null)
    openClientIdRef.current = viewingClient?.id || null
    const fetchSites = async () => {

        try {
            const requestedClientId = viewingClient.id
            const resp = await postWithAuth('fetchSites', { orgId: activeOrg.id, clientId: requestedClientId })
            if (openClientIdRef.current !== requestedClientId) return

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setSites(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    const fetchWorkers = async () => {
        try {
            const resp = await postWithAuth('fetchWorkers', { orgId: activeOrg.id, selectionOnly: true })

            if (resp && resp.err) {
                toast.error(resp.err)
                return
            }

            if (resp && resp.data) {
                setWorkers(resp.data)
            }
        } catch (e) {
            toast.error(e.message)
        }
    }

    useEffect(() => {
        if (viewingClient) {
            setInformation()
            if (viewingClient.id) {
                fetchSites()
            }
        }
    }, [viewingClient])


    const setInformation = () => {
        setAbnVerified(false)
        setCompanyName(viewingClient.name || '')
        setBusinessType(viewingClient.businessType || 'company')
        setAbn(normalizeAbn(viewingClient.abn || ''))
        setAddress(viewingClient.address || '')
        setCoordinates(viewingClient.coordinates || [])
        setContactNo(viewingClient.contactNo || '')
        setRateDayEquipmentCompany(viewingClient.rateDayEquipment || ['', '', '', '', '', '', '', '', '', ''])
        setCustomEquipmentTypes(Array.isArray(viewingClient.customEquipmentTypes) ? viewingClient.customEquipmentTypes : [])
        setRemovedEquipmentTypes(Array.isArray(viewingClient.removedEquipmentTypes) ? viewingClient.removedEquipmentTypes : [])
        setRenamedEquipmentTypes(viewingClient.renamedEquipmentTypes && typeof viewingClient.renamedEquipmentTypes === 'object' ? viewingClient.renamedEquipmentTypes : {})
        setCompanyLabourRates(normalizeLabourRates({
            labourRates: viewingClient.labourRates,
            legacyRates: buildLegacyLabourRateFields(viewingClient),
            workerTypes,
        }))

        const storedContacts = Array.isArray(viewingClient.contacts) ? viewingClient.contacts : []
        const storedPrimaryContact = storedContacts.find(contact => contact.isPrimary)
            || storedContacts.find(contact => (
                viewingClient.primaryEmail
                && String(contact.email || '').toLowerCase() === String(viewingClient.primaryEmail).toLowerCase()
            ))
        const primaryContactRecord = viewingClient.primaryEmail && !storedPrimaryContact
            ? {
                id: `primary-contact-${viewingClient.id || 'draft'}`,
                firstName: viewingClient.primaryFirstName || '',
                lastName: viewingClient.primaryLastName || '',
                contactNo: viewingClient.primaryContactNo || '',
                email: viewingClient.primaryEmail || '',
                isPrimary: true,
            }
            : null
        const savedContacts = primaryContactRecord
            ? [primaryContactRecord, ...storedContacts]
            : storedContacts
        setFirstName(viewingClient.primaryFirstName || '')
        setLastName(viewingClient.primaryLastName || '')
        setPrimaryContactNo(viewingClient.primaryContactNo || savedContacts.find(contact => contact.isPrimary)?.contactNo || '')
        setEmail(viewingClient.primaryEmail || '')
        setAdditionalContacts(savedContacts)
        setPrimaryContactId((storedPrimaryContact || primaryContactRecord)?.id || '')
        setInvoiceEmail(viewingClient.invoiceEmail || '')
        setInvoiceCcEmails(viewingClient.invoiceCcEmails || '')
        setInvoiceBccEmails(viewingClient.invoiceBccEmails || '')
        setInvoiceDueDays(String(viewingClient.invoiceDueDays ?? 7))
        setInvoiceDueTerm(clientInvoiceDueTerm(viewingClient))
    }

    const handleAbnLookup = async () => {
        const normalizedAbn = normalizeAbn(abn)
        if (normalizedAbn.length !== 11) return

        setAbnVerifying(true)
        setAbnVerified(false)
        try {
            const resp = await postWithAuth('lookupAbn', { abn: normalizedAbn })
            const verified = !resp?.err && Array.isArray(resp?.data?.options) && resp.data.options.length > 0
            setAbnVerified(verified)
            if (!verified) toast.error('ABN could not be verified')
        } catch (e) {
            toast.error(e.message)
        } finally {
            setAbnVerifying(false)
        }
    }


    useEffect(() => {
        if (viewingSite) {
            setInformationSite()
        }
    }, [viewingSite])


    const setInformationSite = () => {
        setClientAccessContacts(Array.isArray(viewingSite.clientAccessContacts) ? viewingSite.clientAccessContacts : [])
        setSiteName(viewingSite.name || '')
        setSiteAddress(viewingSite.address || '')
        setSiteCoordinates(viewingSite.coordinates || [])
        setManagerFirstName(viewingSite.managerFirstName || '')
        setManagerLastName(viewingSite.managerLastName || '')
        setManagerEmail(viewingSite.managerEmail || '')
        setSendTimesheetsByEmail(!!viewingSite.sendTimesheetsByEmail)
        setTimesheetEmailFrequency(normalizeTimesheetEmailFrequency(viewingSite.timesheetEmailFrequency))
        setTimesheetEmailRecipients(viewingSite.timesheetEmailRecipients || '')
        setRateDayEquipment(viewingSite.rateDayEquipment || ['', '', '', '', '', '', '', '', '', ''])
        setSiteLabourRates(normalizeLabourRates({
            labourRates: viewingSite.labourRates,
            legacyRates: buildLegacyLabourRateFields(viewingSite),
            workerTypes,
        }))
        setApplyWorkerRatesToSite(!!viewingSite.applyWorkerRatesToSite)
        setSiteEmployeeRates(normalizeEmployeeRates(viewingSite.siteEmployeeRates))

        setManagerContactNo(viewingSite.managerContactNo || '')

        setPictures(viewingSite.pictures || [])
        setWhsDocuments(Array.isArray(viewingSite.whsDocuments) ? viewingSite.whsDocuments : [])
        setWorkerUploads(viewingSite.workerUploads || [])
        setSiteArchived(!!viewingSite.archived)
        setPictureIncludeInNewAllocation(false)
    }


    const getWorkerName = (workerId) => {
        const worker = workers.find(r => r.id === workerId)
        if (!worker) return workerId || 'Worker'
        return formatName(`${worker.firstName || ''} ${worker.lastName || ''}`).trim()
    }

    // Only one client save at a time: a second click while a new client is being
    // created would otherwise create another copy. A new client's id is kept so a
    // retry after a partial failure updates it rather than creating a second one.
    const savingClientRef = useRef(false)
    const createdClientIdRef = useRef('')
    const savedDraftSiteIdsRef = useRef(new Set())
    const handleSaveBusinessInfo = async () => {
        if (savingClientRef.current) return
        savingClientRef.current = true
        try {
            await saveBusinessInfoNow()
        } finally {
            savingClientRef.current = false
        }
    }

    const saveBusinessInfoNow = async () => {

        try {
            const invalidDraftSite = sites.find(site =>
                isDraftSiteId(site.id)
                && (
                    !String(site?.name || '').trim()
                    || !String(site?.address || '').trim()
                    || !String(site?.managerFirstName || '').trim()
                    || !String(site?.managerLastName || '').trim()
                    || !String(site?.managerEmail || '').trim()
                    || !String(site?.managerContactNo || '').trim()
                )
            )
            if (invalidDraftSite) {
                toast.error('Complete all required fields for each site before saving the client')
                return
            }

            setLoading(true)
            const existingClientId = viewingClient.id || createdClientIdRef.current
            const isNewClientRecord = !viewingClient.id

            const payload = {
                id: existingClientId || undefined,
                name: companyName,
                businessType,
                abn: businessType === 'company' ? normalizeAbn(abn) : '',
                address,
                coordinates,
                contactNo,
                rateDayEquipment: rateDayEquipmentCompany,
                labourRates: companyLabourRates,
                customEquipmentTypes,
                removedEquipmentTypes,
                renamedEquipmentTypes,

                primaryFirstName: firstName,
                primaryLastName: lastName,
                primaryContactNo,
                primaryEmail: email,
                contacts: additionalContacts.map(contact => ({
                    ...contact,
                    isPrimary: contact.id === primaryContactId,
                })),
                invoiceEmail,
                invoiceCcEmails,
                invoiceBccEmails,
                invoiceDueDays,
                invoiceDueTerm,
                // The client keeps only a site summary; draft sites (a new client's)
                // are created from state below once the client has an id.
                sites: siteSummaries(sites),
                orgId: activeOrg.id,
            }

            if (['receipt', 'endOfMonth30'].includes(invoiceDueTerm) && !invoiceDueTermsSupported) {
                    throw new Error('This invoice due period requires the server update before it can be saved.')
                }
                const resp = await postWithAuth('saveClientInfo', payload)
            if (resp?.err) throw new Error(resp.err)

            if (isNewClientRecord) {
                const clientId = existingClientId || resp.data
                createdClientIdRef.current = clientId
                // Each draft site is created once, even if this save is retried.
                const draftSites = sites.filter(site => isDraftSiteId(site.id) && !savedDraftSiteIdsRef.current.has(site.id))

                if (draftSites.length > 0) {
                    await Promise.all(draftSites.map(async (site) => {
                        const siteResp = await postWithAuth('saveSiteInfo', {
                            ...site,
                            id: '',
                            clientId,
                            orgId: activeOrg.id,
                        })
                        if (siteResp?.err) throw new Error(siteResp.err)
                        savedDraftSiteIdsRef.current.add(site.id)
                    }))
                }
                createdClientIdRef.current = ''
                savedDraftSiteIdsRef.current = new Set()

                await fetchClients()
                await fetchInvoices()
                setViewingSite(false)
                setViewingClient()
                setSites([])
            } else {
                await fetchClients()
                await fetchInvoices()
                closeClientPanel()
            }

            setEditing(false)
            setSuccessMessage({
                title: 'Client saved',
                message: 'Client information saved successfully.',
            })
            setLoading(false)

        } catch (e) {
            toast.error(e.message)
            setLoading(false)
            // The client itself was created but a draft site failed: show it in the
            // list so a retry after closing the form edits it instead of adding a copy.
            if (!viewingClient?.id && createdClientIdRef.current) fetchClients()
        }

    }

    // A new site has no id until its first save. Uploading a photo or document saves
    // the site and keeps it open, so later saves must update the site that first
    // save created instead of creating another one. Saves also run one at a time:
    // a second save started while the first is still creating the site would not
    // know its id yet.
    const createdSiteId = useRef('')
    const siteSaveQueue = useRef(Promise.resolve())
    const handleSaveSiteInfo = (saveOptions = {}) => {
        const run = siteSaveQueue.current.then(() => saveSiteInfoNow(saveOptions))
        siteSaveQueue.current = run.catch(() => {})
        return run
    }

    const saveSiteInfoNow = async (saveOptions = {}) => {

        try {
            const keepSiteOpen = saveOptions?.keepSiteOpen === true
            const documentsToSave = Array.isArray(saveOptions?.whsDocumentsOverride)
                ? saveOptions.whsDocumentsOverride
                : whsDocuments
            // A saved site's documents are changed one at a time (saveSiteDocument), so a
            // normal save leaves them out: sending the list loaded when the site was
            // opened would delete documents added since (from mobile or the Forms page).
            // Only a new site, or an explicit list, sends them.
            const siteAlreadySaved = !!(viewingSite.id || createdSiteId.current) && !isDraftSiteId(viewingSite.id)
            const picturesToSave = Array.isArray(saveOptions?.picturesOverride)
                ? saveOptions.picturesOverride
                : siteAlreadySaved ? undefined : pictures
            const normalizedTimesheetEmailFrequency = normalizeTimesheetEmailFrequency(timesheetEmailFrequency)
            const normalizedTimesheetEmailRecipients = String(timesheetEmailRecipients || '').trim()
            const trimmedSiteName = String(siteName || '').trim()
            const trimmedSiteAddress = String(siteAddress || '').trim()
            const trimmedManagerFirstName = String(managerFirstName || '').trim()
            const trimmedManagerLastName = String(managerLastName || '').trim()
            const trimmedManagerEmail = String(managerEmail || '').trim().toLowerCase()
            const trimmedManagerContactNo = String(managerContactNo || '').trim()

            if (!trimmedSiteName) {
                toast.error('Enter a site name')
                return
            }
            if (!trimmedSiteAddress) {
                toast.error('Enter a site address')
                return
            }
            if (!trimmedManagerFirstName || !trimmedManagerLastName || !trimmedManagerEmail || !trimmedManagerContactNo) {
                toast.error('Complete all required site manager fields')
                return
            }

            if (sendTimesheetsByEmail && !normalizedTimesheetEmailFrequency) {
                toast.error('Select a timesheet email frequency')
                return
            }
            if (sendTimesheetsByEmail && !normalizedTimesheetEmailRecipients) {
                toast.error('Enter at least one send to email')
                return
            }
            setLoading(true)

            const sitePayload = {
                id: viewingSite.id || createdSiteId.current || undefined,
                name: trimmedSiteName,
                ...(picturesToSave === undefined ? {} : { pictures: picturesToSave }),
                whsDocuments: documentsToSave,
                address: trimmedSiteAddress,
                coordinates: siteCoordinates,
                rateDayEquipment,
                labourRates: siteLabourRates,
                applyWorkerRatesToSite,
                siteEmployeeRates,

                managerFirstName: trimmedManagerFirstName,
                managerLastName: trimmedManagerLastName,
                managerEmail: trimmedManagerEmail,
                managerContactNo: trimmedManagerContactNo,
                sendTimesheetsByEmail,
                timesheetEmailFrequency: sendTimesheetsByEmail ? normalizedTimesheetEmailFrequency : '',
                timesheetEmailRecipients: sendTimesheetsByEmail ? normalizedTimesheetEmailRecipients : '',
                archived: !!siteArchived,
                clientId: viewingClient.id,
                orgId: activeOrg.id,
            }

            if (!viewingClient.id) {
                const draftId = isDraftSiteId(viewingSite.id) ? viewingSite.id : `draft-site-${Date.now()}`
                const draftSite = { ...sitePayload, id: draftId }
                setSites((currentSites) => (
                    currentSites.some(site => site.id === draftId)
                        ? currentSites.map(site => site.id === draftId ? draftSite : site)
                        : [...currentSites, draftSite]
                ))
                setLoading(false)
                setEditingSite(false)
                if (keepSiteOpen) {
                    setViewingSite(draftSite)
                } else {
                    setViewingSite(false)
                }
                return true
            }

            const siteSaveResult = await postWithAuth('saveSiteInfo', sitePayload)
            if (siteSaveResult?.err) throw new Error(siteSaveResult.err)
            // Creating a site returns its new id; keep it so the next save updates it.
            if (!sitePayload.id && typeof siteSaveResult?.data === 'string' && siteSaveResult.data !== 'Site saved') {
                sitePayload.id = siteSaveResult.data
                createdSiteId.current = siteSaveResult.data
            }

            // The site manager gets Site Hub access through Site Portal Access. When the
            // manager of a site that uses Site Hub changes, save its access again so the
            // new manager is invited and the previous one loses this site.
            const managerChanged = String(viewingSite.managerEmail || '').trim().toLowerCase() !== trimmedManagerEmail
            if (managerChanged && (viewingSite.portalAccessEmails || []).length) {
                const accessResult = await postWithAuth('saveSitePortalAccess', { orgId: activeOrg.id, siteId: sitePayload.id, contacts: viewingSite.clientAccessContacts || [] }).catch(error => ({ err: error.message }))
                if (accessResult?.err) {
                    toast.error('The site was saved, but Site Hub access was not updated for the new site manager. Open -/+ Access and press Save Access.')
                } else {
                    sitePayload.portalAccessEmails = accessResult?.data?.portalAccessEmails || []
                    showConfirmation('Site Hub access moved to the new site manager.')
                }
            }

            setLoading(false)
            setEditingSite(false)
            // The list is updated either way: a site reopened from it (the URL keeps
            // its id) must show what was just saved, not the version from before.
            setSites((currentSites) => currentSites.map((site) => site.id === sitePayload.id ? { ...site, ...sitePayload } : site))
            if (keepSiteOpen) {
                setViewingSite((currentSite) => ({ ...currentSite, ...sitePayload }))
            } else {
                closeSitePanel()
            }
            fetchSites()
            return true
        } catch (e) {
            toast.error(e.message)
            setLoading(false)
            return false
        }

    }

    const applyClientRatesToAllSites = async () => {
        if (!viewingClient?.id) return
        if (!window.confirm(`Apply this client's labour and equipment rates to all ${sites.length} sites?`)) return
        try {
            setLoading(true)
            const resp = await postWithAuth('applyClientRatesToAllSites', { clientId: viewingClient.id, orgId: activeOrg.id })
            if (resp?.err) throw new Error(resp.err)
            await fetchSites()
            showConfirmation(`Rates applied to ${Number(resp?.data?.updated || 0)} sites`)
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
        }
    }

    const toggleSiteArchive = async (siteId) => {
        const targetSite = sites.find((site) => site.id === siteId)
        if (!targetSite) return

        try {
            setLoading(true)
            await postWithAuth('saveSiteInfo', {
                id: targetSite.id,
                name: targetSite.name || '',
                // Documents are left out: archiving must not overwrite documents added
                // since this list was loaded (the server keeps them when absent).
                address: targetSite.address || '',
                coordinates: targetSite.coordinates || [],
                rateDayEquipment: targetSite.rateDayEquipment || ['', '', '', '', '', '', '', '', '', ''],
                labourRates: normalizeLabourRates({
                    labourRates: targetSite.labourRates,
                    legacyRates: buildLegacyLabourRateFields(targetSite),
                    workerTypes,
                }),
                managerFirstName: targetSite.managerFirstName || '',
                managerLastName: targetSite.managerLastName || '',
                managerEmail: String(targetSite.managerEmail || '').trim().toLowerCase(),
                managerContactNo: targetSite.managerContactNo || '',
                sendTimesheetsByEmail: !!targetSite.sendTimesheetsByEmail,
                timesheetEmailFrequency: normalizeTimesheetEmailFrequency(targetSite.timesheetEmailFrequency),
                timesheetEmailRecipients: targetSite.timesheetEmailRecipients || '',
                archived: !targetSite.archived,
                clientId: viewingClient.id,
                orgId: activeOrg.id,
            })
            await fetchSites()
            if (viewingSite?.id === siteId) {
                setViewingSite({ ...targetSite, archived: !targetSite.archived })
            }
        } catch (e) {
            toast.error(e.message)
        } finally {
            setLoading(false)
        }
    }

    const closeSiteDocumentModal = () => {
        setAddPicture(false)
        setShowSiteDocumentCancelConfirm(false)
        setAddPictureIndex(-1)
        setPicture('')
        setPictureName('')
        setPictureDate('')
        setPictureNote('')
        setPictureIncludeInNewAllocation(false)
    }

    const requestSiteDocumentCancel = () => {
        setShowSiteDocumentCancelConfirm(true)
    }

    const openEditSiteDocument = (row, index) => {
        const parsedDate = row?.date ? new Date(row.date) : ''
        setAddPictureIndex(index)
        setPicture(row?.picture || '')
        setPictureName(row?.name || getDocumentFileName(row?.picture))
        setPictureDate(parsedDate && !Number.isNaN(parsedDate.getTime()) ? parsedDate : '')
        setPictureNote(row?.note || row?.category || '')
        setPictureIncludeInNewAllocation(!!row?.includeInNewAllocation)
        setAddPicture(true)
    }

    const openSiteFormDocument = (row, index, readOnly) => {
        setWhsDocumentType(getSiteFormType(row) || 'Safe Work Method Statement')
        setWhsDocumentSubcategory(getSiteFormCategory(row) || 'General Labour')
        setWhsFormInitialData(getSiteFormData(row))
        setWhsFormEditingId(row.id || null)
        setWhsSiteDocumentIndex(index)
        setWhsFormReadOnly(readOnly)
        setShowWhsFormModal(true)
    }

    // The saved site this document belongs to, if any. A new site (or a new
    // client's draft site) has none yet and is created by the full site save.
    const savedSiteIdForDocuments = () => {
        const siteId = viewingSite?.id || createdSiteId.current
        return viewingClient?.id && siteId && !isDraftSiteId(siteId) ? siteId : ''
    }

    // Saves one document on the stored site. The server applies it inside a
    // transaction, so documents added elsewhere (mobile, the Documentation page)
    // since this site was opened are kept, and no other site field is written.
    const saveSiteDocumentNow = async (siteId, document) => {
        const resp = await postWithAuth('saveSiteDocument', { orgId: activeOrg.id, siteId, document })
        if (resp?.err) throw new Error(resp.err)
        const savedPictures = Array.isArray(resp?.data?.pictures)
            ? resp.data.pictures
            : [document, ...pictures.filter(row => row?.id !== document.id)]
        setPictures(savedPictures)
        setSites(currentSites => currentSites.map(site => site.id === siteId ? { ...site, pictures: savedPictures } : site))
        return savedPictures
    }

    const handleAddPicture = async () => {
        const isEditingDocument = addPictureIndex >= 0
        const existingRow = isEditingDocument ? pictures[addPictureIndex] : null
        const nextPicture = {
            name: pictureName || getDocumentFileName(picture),
            note: pictureNote,
            picture: picture,
            date: pictureDate ? pictureDate.toString() : '',
            includeInNewAllocation: !!pictureIncludeInNewAllocation,
        }
        const savedSiteId = savedSiteIdForDocuments()
        // Older rows have no id to replace by; those still go through the full save.
        if (savedSiteId && (!isEditingDocument || existingRow?.id)) {
            try {
                setLoading(true)
                await saveSiteDocumentNow(savedSiteId, {
                    ...(existingRow || {}),
                    ...nextPicture,
                    id: existingRow?.id || `site-document-${Date.now()}-${randomString(6)}`,
                })
                closeSiteDocumentModal()
                setSuccessMessage({
                    title: isEditingDocument ? 'Site document updated' : 'Site document saved',
                    message: isEditingDocument ? 'Site document updated successfully.' : 'Site document saved successfully.',
                })
            } catch (e) {
                toast.error(e.message || 'Unable to save this site document.')
            } finally {
                setLoading(false)
            }
            return
        }
        // Give every document an id, so it can be changed on its own from now on.
        const withId = { ...nextPicture, id: existingRow?.id || `site-document-${Date.now()}-${randomString(6)}` }
        const nextPictures = isEditingDocument
            ? pictures.map((row, idx) => idx === addPictureIndex ? { ...row, ...withId } : row)
            : [...pictures, withId]
        setPictures(nextPictures)
        closeSiteDocumentModal()
        if (savedSiteId) {
            // An older document with no id on a saved site: send the whole list once.
            const saved = await handleSaveSiteInfo({ keepSiteOpen: true, picturesOverride: nextPictures })
            if (saved) setSuccessMessage({ title: 'Site document updated', message: 'Site document updated successfully.' })
            else setPictures(pictures)
            return
        }
        setTriggerSaveInformation(isEditingDocument ? 'updated' : 'saved')
    }

    const copyPrimaryContactToSite = () => {
        setManagerFirstName(firstName || '')
        setManagerLastName(lastName || '')
        setManagerEmail(email || '')
        setManagerContactNo(contactNo || '')
    }

    const renderPreviewContent = (file, label) => {
        if (!file?.url) return <span className='clientEmptyValue'>No preview available</span>
        const isPdf = file.pdf || normalizeSearch(file.url || '').includes('.pdf')
        return isPdf
            ? <a href={file.url} target='_blank' rel='noreferrer'>Open PDF</a>
            : <img alt={label} src={file.url} className='clientPreviewImage' />
    }


    const clientsSort = useTableSort(clients, { key: 'name', direction: 'ascending' });
    const clientSearchQuery = normalizeSearch(clientSearch.trim())
    const visibleClients = clientSearchQuery
        ? clientsSort.sortedData.filter(client => normalizeSearch(`${client.clientNumber || ''} ${client.name || ''} ${client.address || ''}`).includes(clientSearchQuery))
        : clientsSort.sortedData
    const sitesSort = useTableSort(sites, { key: 'name', direction: 'ascending' });
    const clientInvoices = useMemo(
        () => invoices
            .filter(invoice => invoice.client?.id === viewingClient?.id)
            .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0)),
        [invoices, viewingClient]
    )
    const clientInvoicePageSize = 10
    const clientInvoicePageCount = Math.max(1, Math.ceil(clientInvoices.length / clientInvoicePageSize))
    const paginatedClientInvoices = useMemo(
        () => clientInvoices.slice((clientInvoicePage - 1) * clientInvoicePageSize, clientInvoicePage * clientInvoicePageSize),
        [clientInvoices, clientInvoicePage]
    )

    useEffect(() => {
        setClientInvoicePage(1)
        setExpandedClientInvoiceId('')
    }, [viewingClient?.id])

    useEffect(() => {
        setClientInvoicePage(current => Math.min(current, clientInvoicePageCount))
    }, [clientInvoicePageCount])

    const requiresAbn = businessType === 'company'
    const isNewClient = !!viewingClient && !viewingClient.id && editing
    const isNewSite = !!viewingSite && !viewingSite.id && editingSite
    const clientEquipmentRows = [
        ...equipmentOptions
            .filter(item => !removedEquipmentTypes.includes(item))
            .map(item => ({ name: renamedEquipmentTypes[item] || item, sourceName: item, custom: false })),
        ...customEquipmentTypes.map(item => ({ name: item, sourceName: item, custom: true })),
    ]
    const clientEquipmentOptions = clientEquipmentRows.map(item => item.name)
    // Rates are stored by position: built-in types keep their fixed place in
    // equipmentOptions (sites and invoices read them there), and custom types
    // follow in order. A removed built-in type leaves a gap, never a shift.
    const getEquipmentRateIndex = (row) => (row.custom
        ? equipmentOptions.length + customEquipmentTypes.indexOf(row.sourceName)
        : equipmentOptions.indexOf(row.sourceName))
    const addCustomEquipmentType = () => {
        const name = newEquipmentTypeName.trim()
        if (!name) return
        if (clientEquipmentOptions.some(item => item.toLowerCase() === name.toLowerCase() && item !== equipmentTypeEditing?.name)) {
            toast.error('This equipment type already exists.')
            return
        }
        if (equipmentTypeEditing) {
            if (equipmentTypeEditing.custom) {
                setCustomEquipmentTypes(current => current.map(item => item === equipmentTypeEditing.sourceName ? name : item))
            } else {
                setRenamedEquipmentTypes(current => ({ ...current, [equipmentTypeEditing.sourceName]: name }))
            }
            setEquipmentTypeEditing(null)
            setNewEquipmentTypeName('')
            return
        }
        setCustomEquipmentTypes(current => [...current, name])
        setRateDayEquipmentCompany(current => [...current, ''])
        setNewEquipmentTypeName('')
    }
    const deleteClientEquipmentType = (item) => {
        const rateIndex = getEquipmentRateIndex(item)
        if (item.custom) {
            setCustomEquipmentTypes(current => current.filter(name => name !== item.sourceName))
            // Custom rates sit after the built-ins, so later custom types move up with their names.
            if (rateIndex >= equipmentOptions.length) {
                setRateDayEquipmentCompany(current => current.filter((_, index) => index !== rateIndex))
            }
        } else {
            setRemovedEquipmentTypes(current => [...current, item.sourceName])
            if (rateIndex >= 0) {
                setRateDayEquipmentCompany(current => current.map((rate, index) => index === rateIndex ? '' : rate))
            }
        }
    }
    const additionalInvoiceEmails = String(invoiceCcEmails || '').split(',').map(item => item.trim()).filter(Boolean)
    const getInvoiceStatusLabel = (invoice) => {
        const status = String(invoice?.status || 'Draft').trim() || 'Draft'
        return status
    }
    const addInvoiceEmail = () => {
        const nextEmail = newInvoiceEmail.trim()
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail)) {
            toast.error('Enter a valid email address.')
            return
        }
        const emailsToCompare = additionalInvoiceEmails.filter((_, index) => index !== invoiceEmailEditingIndex)
        if ([invoiceEmail, ...emailsToCompare].some(item => item.toLowerCase() === nextEmail.toLowerCase())) {
            toast.error('This invoice email already exists.')
            return
        }
        if (invoiceEmailEditingIndex !== null) {
            setInvoiceCcEmails(additionalInvoiceEmails.map((item, index) => index === invoiceEmailEditingIndex ? nextEmail : item).join(', '))
            setInvoiceEmailEditingIndex(null)
        } else {
            setInvoiceCcEmails([...additionalInvoiceEmails, nextEmail].join(', '))
        }
        setNewInvoiceEmail('')
    }
    const deleteInvoiceEmail = (index) => {
        setInvoiceCcEmails(additionalInvoiceEmails.filter((_, emailIndex) => emailIndex !== index).join(', '))
        if (invoiceEmailEditingIndex === index) {
            setInvoiceEmailEditingIndex(null)
            setNewInvoiceEmail('')
        }
    }

    const closeWhsFormModal = () => {
        setShowWhsFormModal(false)
        setWhsFormInitialData(null)
        setWhsFormEditingId(null)
        setWhsSiteDocumentIndex(null)
        setWhsFormReadOnly(false)
    }

    const handleWhsFormSaved = async (entry, pdfBlob, formSaveOptions = {}) => {
        if (whsSiteDocumentIndex !== null) {
            const originalDocument = pictures[whsSiteDocumentIndex]
            if (!originalDocument) return

            try {
                setLoading(true)
                const documentId = originalDocument.id || originalDocument.formData?.id || entry.id
                let updatedPicture = originalDocument.picture
                if (pdfBlob) {
                    const uploaded = await uploadFileToStorage({
                        file: pdfBlob,
                        path: `SiteDocs/forms/${activeOrg.id}/${viewingSite.id}/${documentId}.pdf`,
                        mimeType: 'application/pdf',
                        fallbackName: `${documentId}.pdf`,
                        orgId: activeOrg.id,
                    })
                    updatedPicture = {
                        ...(typeof originalDocument.picture === 'object' ? originalDocument.picture : {}),
                        ...uploaded,
                        pdf: true,
                        confidential: false,
                    }
                }

                const updatedDocument = {
                    ...originalDocument,
                    ...entry,
                    id: documentId,
                    name: originalDocument.name || entry.name,
                    date: originalDocument.date || entry.date,
                    picture: updatedPicture,
                    includeInNewAllocation: !!formSaveOptions.includeInNewAllocation,
                }
                const savedSiteId = savedSiteIdForDocuments()
                if (savedSiteId && originalDocument.id) {
                    await saveSiteDocumentNow(savedSiteId, updatedDocument)
                    closeWhsFormModal()
                    setSuccessMessage({
                        title: 'Site document updated',
                        message: `${updatedDocument.name} was updated successfully.`,
                    })
                    return
                }

                const nextPictures = pictures.map((document, index) => (
                    index === whsSiteDocumentIndex ? updatedDocument : document
                ))

                setPictures(nextPictures)
                const saved = await handleSaveSiteInfo({
                    keepSiteOpen: true,
                    picturesOverride: nextPictures,
                })
                if (!saved) {
                    setPictures(pictures)
                    return
                }

                closeWhsFormModal()
                setSuccessMessage({
                    title: 'Site document updated',
                    message: `${updatedDocument.name} was updated successfully.`,
                })
            } catch (error) {
                toast.error(error.message || 'Unable to update this site document.')
            } finally {
                setLoading(false)
            }
            return
        }

        const whsRecord = {
            ...entry,
            id: whsFormEditingId || entry.id,
            date: entry.date || entry.savedAt || new Date().toISOString(),
        }
        const nextWhsDocuments = whsFormEditingId
            ? whsDocuments.map((document) => document.id === whsFormEditingId ? whsRecord : document)
            : [whsRecord, ...whsDocuments]

        setWhsDocuments(nextWhsDocuments)
        const saved = await handleSaveSiteInfo({
            keepSiteOpen: true,
            whsDocumentsOverride: nextWhsDocuments,
        })

        if (!saved) {
            setWhsDocuments(whsDocuments)
            return
        }

        closeWhsFormModal()
        setSuccessMessage({
            title: 'WHS document saved',
            message: `${whsRecord.name} was saved to this site.`,
        })
    }

    return (<div>
        {/* Access is saved by its own endpoint, which creates the logins, so it is managed on a saved site outside edit mode. */}
        {managingPortalAccess && viewingSite && <SitePortalAccess orgId={activeOrg.id} site={viewingSite} onClose={() => setManagingPortalAccess(false)} onSaved={saved => {
            setViewingSite(current => ({ ...current, ...saved }))
            setSites(current => current.map(site => site.id === viewingSite.id ? { ...site, ...saved } : site))
        }} />}
        {siteImagePreview && <ModalComponent showModal setShowModal={() => setSiteImagePreview(null)} customMaxWidth='760px' customOverlayStyle={{ zIndex: 1200 }}><div className='clientSiteImagePreviewHeader'><h2>Site Image</h2><Button variant="secondary" size="md" type='button' className='secondaryButton' onClick={() => setSiteImagePreview(null)}>Cancel</Button></div><div className='clientSiteImagePreviewBody'>{renderPreviewContent(siteImagePreview.file, siteImagePreview.label)}</div></ModalComponent>}
        <Header />
        <LoadingPopup visible={loading} message="Loading..." />
        <div className='pageShell'>
            <Sidebar />
            <PageContent as="div" className='pageContent pageContentPadded clientPageContent'>
                <PageHeader title="Clients" subtitle={<>{viewingClient && !viewingSite && !isNewClient ? formatCompanyName(companyName || viewingClient.name || 'Client details') : 'Manage client companies and their details'}</>} />
                <CompactSuccessModal success={successMessage} setSuccess={setSuccessMessage} />
                <ModalComponent
                    showModal={showAddContactModal}
                    setShowModal={(isOpen) => {
                        if (!isOpen) closeAddContactModal()
                    }}
                    customMaxWidth='440px'
                    customContentStyle={{
                        minWidth: 0,
                        width: 'min(440px, calc(100vw - 20px))',
                        padding: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div className='clientContactDialog'>
                        <div className='clientContactDialogHeader'>
                            <h2>{contactManagerMode ? 'Edit Contacts' : (contactEditingId ? 'Edit Contact' : 'Add New Contact')}</h2>
                            {!contactManagerMode && <Button variant="secondary" size="md" type='button' aria-label="Cancel" onClick={closeAddContactModal}>Cancel</Button>}
                        </div>
                        {contactPendingDelete && (
                            <div className='clientEquipmentDeleteConfirm'>
                                <h2 className='modalTitle'>Are you sure?</h2>
                                <p>This will remove {formatName(contactPendingDelete.firstName)} {formatName(contactPendingDelete.lastName)} from the contact list.</p>
                                <div className='modalFooter'>
                                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setContactPendingDelete(null)}>Cancel</Button>
                                    <Button variant="danger" size="md"
                                        type='button'
                                        className='modalDangerButton'
                                        onClick={() => {
                                            setAdditionalContacts((contacts) => contacts.filter((contact) => contact.id !== contactPendingDelete.id))
                                            if (primaryContactId === contactPendingDelete.id) setPrimaryContactId('')
                                            setContactPendingDelete(null)
                                        }}
                                    >
                                        Yes
                                    </Button>
                                </div>
                            </div>
                        )}
                        {contactManagerMode && (
                            <div className='clientEquipmentTypeList clientContactManagerList'>
                                <div className='clientEquipmentTypeListHeading'>
                                    <span>Contact</span>
                                    <span>Action</span>
                                </div>
                                {additionalContacts.map((contact) => (
                                    <div className='clientEquipmentTypeListRow' key={contact.id || contact.email}>
                                        <span>{formatName(contact.firstName)} {formatName(contact.lastName)}</span>
                                        <div className='clientEquipmentTypeRowActions'>
                                            <Button data-action-tone="edit" variant="secondary" size="md"
                                                type='button'
                                                className='clientEquipmentTypeEditAction'
                                                onClick={() => {
                                                    setContactEditingId(contact.id)
                                                    setContactFirstName(contact.firstName || '')
                                                    setContactLastName(contact.lastName || '')
                                                    setContactEmail(contact.email || '')
                                                    setContactNumber(contact.contactNo || '')
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button data-action-tone="delete" variant="danger" size="md"  type='button' onClick={() => setContactPendingDelete(contact)}>Delete</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        <div className='clientContactDialogBody'>
                            {contactManagerMode && <h3 className='clientContactManagerFormTitle'>{contactEditingId ? 'Edit Contact' : 'Add New'}</h3>}
                            <div className='clientContactNameGrid'>
                                <Input value={contactFirstName} setValue={setContactFirstName} label='First Name' required />
                                <Input value={contactLastName} setValue={setContactLastName} label='Last Name' required />
                            </div>
                            <Input type='email' value={contactEmail} setValue={setContactEmail} label='Email' required />
                            <div>
                                <FieldLabel className='fieldLabel'>Contact Number <span className='clientContactOptional'>(if applicable)</span></FieldLabel>
                                <UiInput
                                    aria-label='Contact Number'
                                    value={contactNumber}
                                    onChange={(event) => setContactNumber(event.target.value)}
                                    placeholder='+61'
                                />
                            </div>
                        </div>
                        <div className='clientContactDialogFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={closeAddContactModal}>{contactManagerMode ? 'Cancel' : 'Cancel'}</Button>
                            <Button data-action-tone="save" variant={contactEditingId ? 'soft' : 'create'} size="md"
                                type='button'
                                disabled={!contactFirstName.trim() || !contactLastName.trim() || !contactEmail.trim() || loading}
                                onClick={handleAddContact}
                            >
                                {contactEditingId ? <EditIcon /> : <PlusIcon />}
                                {contactEditingId ? 'Save Contact' : 'Add Contact'}
                            </Button>
                        </div>
                    </div>
                </ModalComponent>
                <ModalComponent
                    showModal={addPicture}
                    setShowModal={(isOpen) => {
                        if (!isOpen) requestSiteDocumentCancel()
                    }}
                    customMaxWidth='500px'
                    customContentStyle={{
                        minWidth: 0,
                        width: 'min(500px, calc(100vw - 20px))',
                        padding: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div className='clientWhsDocumentDialog clientSiteDocumentDialog'>
                        <div className='clientWhsDocumentDialogHeader'>
                            <h2>{addPictureIndex >= 0 ? 'Edit Site Document' : 'Add Site Document'}</h2>
                        </div>
                        <div className='clientSiteDocumentDialogBody'>
                            <div className='clientSiteFieldGrid clientSiteFieldGridTwo'>
                                <Input disabled={false} value={pictureName} setValue={setPictureName} placeholder='Document name' label='Document Name' required />
                                <Input disabled={false} value={pictureNote} setValue={setPictureNote} placeholder='Note' label='Type/Note' required />
                                <MyCalendar disabled={false} noMaxDate showDateOnly startDate={pictureDate} setStartDate={setPictureDate} label='Date' />
                                <div className='clientSiteFieldSpanFull'>
                                    <MyDropzone key={picture} disabled={false} storageLocation={`SiteDocs/${randomString()}`} label='Document' customMessage='Click here/Drop files here to upload' editing={true} errors={[]} allowPdf name='document' image={picture} updateImage={(doc) => setPicture(doc)} />
                                </div>
                                <div className='clientSiteFieldSpanFull'>
                                    <Checkbox label='Add to allocations' value={pictureIncludeInNewAllocation} setValue={setPictureIncludeInNewAllocation} />
                                </div>
                            </div>
                        </div>
                        <div className='clientWhsDocumentDialogFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={requestSiteDocumentCancel}>Cancel</Button>
                            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='clientModalSaveButton saveWithIcon' disabled={!pictureName || !pictureNote || !picture} onClick={handleAddPicture}>Save</Button>
                        </div>
                    </div>
                </ModalComponent>
                <ModalComponent
                    showModal={showSiteDocumentCancelConfirm}
                    setShowModal={setShowSiteDocumentCancelConfirm}
                    customMaxWidth='300px'
                    customContentStyle={{ minWidth: 0, width: 'min(300px, calc(100vw - 32px))', padding: '14px' }}
                >
                    <div className='allocationConfirmMessage recordConfirmMessage'>
                        <h2 className='modalTitle'>Are you sure?</h2>
                        <p className='modalSubtitle'>Cancel adding this site document?</p>
                        <div className='modalFooter'>
                            <Button variant="secondary" size="md" type='button' className='secondaryButton' onClick={() => setShowSiteDocumentCancelConfirm(false)}>No</Button>
                            <Button variant="danger" size="md" type='button' className='modalDangerButton' onClick={closeSiteDocumentModal}>Yes</Button>
                        </div>
                    </div>
                </ModalComponent>
                <ModalComponent
                    showModal={showAddWhsDocumentModal}
                    setShowModal={setShowAddWhsDocumentModal}
                    customMaxWidth='420px'
                    customContentStyle={{
                        minWidth: 0,
                        width: 'min(420px, calc(100vw - 20px))',
                        padding: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div className='clientWhsDocumentDialog'>
                        <div className='clientWhsDocumentDialogHeader'>
                            <h2>Add WHS Doc</h2>
                            <Button variant="secondary" size="md" type='button' aria-label='Close' onClick={() => setShowAddWhsDocumentModal(false)}>Cancel</Button>
                        </div>
                        <div className='clientWhsDocumentDialogBody'>
                            <Dropdown
                                label='Select Document or Form'
                                placeholder='Select document or form'
                                options={whsDocumentationOptions}
                                value={getDropdownValue(whsDocumentType, whsDocumentationOptions)}
                                setValue={(option) => {
                                    setWhsDocumentType(option?.value || '')
                                    setWhsDocumentSubcategory('')
                                }}
                            />
                            {whsDocumentType === 'Safe Work Method Statement' && (
                                <Dropdown
                                    label='Select Subcategory'
                                    placeholder='Select subcategory'
                                    options={whsSwmsSubcategoryOptions}
                                    value={getDropdownValue(whsDocumentSubcategory, whsSwmsSubcategoryOptions)}
                                    setValue={(option) => {
                                        const nextSubcategory = option?.value || ''
                                        setWhsDocumentSubcategory(nextSubcategory)
                                        if (nextSubcategory) {
                                            setWhsFormInitialData(null)
                                            setWhsFormEditingId(null)
                                            setWhsSiteDocumentIndex(null)
                                            setWhsFormReadOnly(false)
                                            setShowAddWhsDocumentModal(false)
                                            setShowWhsFormModal(true)
                                        }
                                    }}
                                />
                            )}
                        </div>
                        <div className='clientWhsDocumentDialogFooter'>
                            <Button variant="secondary" size="md" type='button' className='secondaryButton' onClick={() => setShowAddWhsDocumentModal(false)}>Cancel</Button>
                        </div>
                    </div>
                </ModalComponent>
                <ModalComponent
                    showModal={showWhsFormModal}
                    setShowModal={(isOpen) => {
                        if (!isOpen) closeWhsFormModal()
                    }}
                    customMaxWidth='1180px'
                    customContentStyle={{
                        minWidth: 0,
                        width: 'min(1180px, calc(100vw - 24px))',
                        maxHeight: '94vh',
                        padding: 0,
                        overflow: 'hidden',
                    }}
                >
                    <div className='clientWhsFormDialog'>
                        <div className='clientWhsFormDialogHeader'>
                            <div>
                                <span>Safe Work Method Statement</span>
                                <h2>{whsDocumentSubcategory}</h2>
                            </div>
                        </div>
                        <div className='clientWhsFormDialogBody'>
                            {showWhsFormModal && (
                                <Documentation
                                    embedded
                                    initialType='Safe Work Method Statement'
                                    initialSwmsType={whsDocumentSubcategory}
                                    initialFormData={whsFormInitialData}
                                    initialReadOnly={whsFormReadOnly}
                                    hideFormHeading={whsSiteDocumentIndex !== null}
                                    initialAttachToAllocations={!!pictures[whsSiteDocumentIndex]?.includeInNewAllocation}
                                    initialProjectDetails={{
                                        clientId: viewingClient?.id || '',
                                        siteId: viewingSite?.id || '',
                                        clientName: companyName || viewingClient?.name || '',
                                        projectName: viewingSite?.name || siteName || '',
                                        address: siteAddress || viewingSite?.address || address || viewingClient?.address || '',
                                        primaryContactName: formatName(`${firstName || viewingClient?.primaryFirstName || ''} ${lastName || viewingClient?.primaryLastName || ''}`).trim(),
                                        primaryContactNumber: primaryContactNo || viewingClient?.primaryContactNo || contactNo || '',
                                    }}
                                    onFormSaved={handleWhsFormSaved}
                                    onCancel={closeWhsFormModal}
                                />
                            )}
                        </div>
                    </div>
                </ModalComponent>
                {viewingClient ? <>
                    {!viewingSite ? <>
                        <div className={`clientProfileSectionStack clientNewClientCompact ${isNewClient ? 'clientNewClientForm' : 'clientExistingProfilePage'}`}>
                            {!isNewClient && (
                                <div className='clientProfilePageToolbar'>
                                    <div className={'clientExistingProfileHeaderActions' + (!editing ? ' clientProfileTopViewActions' : '')}>
                                        {editing ? <>
                                            <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton clientTopActionButton' onClick={() => { setEditing(false); setInformation() }}>Cancel</Button>
                                            <Button data-action-tone="save" variant="soft" size="md"
                                                type='button'
                                                className='clientTopActionButton saveWithIcon'
                                                disabled={(requiresAbn && !abn) || !companyName || !address || coordinates.length === 0 || !primaryContactNo || !firstName || !lastName || !email || !invoiceEmail || loading}
                                                onClick={handleSaveBusinessInfo}
                                            >
                                                Save
                                            </Button>
                                        </> : <>
                                            <Button data-action-tone="back" variant="secondary" size="md"  type='button' className='secondaryButton clientTopActionButton clientProfileBackButton' onClick={closeClientPanel}>Back to Clients</Button>

                                            <Button data-action-tone="edit" variant="secondary" size="md"  type='button' className='clientTopActionButton clientProfileEditButton' onClick={() => setEditing(true)}>Edit</Button>
                                            <Button data-action-tone="delete" variant="danger" size="md"
                                                type='button'
                                                className='secondaryButton clientTopActionButton clientProfileDeleteButton'
                                                disabled={archivingClient || deletingClient}
                                                onClick={handleDeleteClient}
                                            >
                                                Delete
                                            </Button>

<Button data-action-tone="archive" variant="secondary" size="md"
                                                type='button'
                                                className='secondaryButton clientTopActionButton clientProfileArchiveButton'
                                                disabled={archivingClient || deletingClient}
                                                onClick={() => handleArchiveClient(!viewingClient?.archived)}
                                            >
                                                {viewingClient?.archived ? 'Unarchive' : 'Archive'}
                                            </Button>
                                        </>}
                                    </div>
                                </div>
                            )}
                            {isNewClient && (
                                <div className='clientNewClientModalHeader'>
                                    <div>
                                        <h2>New Client</h2>
                                        <p>Add the client’s business, contact, site, account, and rate details.</p>
                                    </div>
                                    <div className='clientNewClientHeaderActions'>
                                        <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton clientTopActionButton' onClick={closeClientPanel}>Cancel</Button>
                                        <Button data-action-tone="save" variant="soft" size="md"
                                            type='button'
                                            className='clientTopActionButton saveWithIcon'
                                            disabled={(requiresAbn && !abn) || !companyName || !address || coordinates.length === 0 || !primaryContactNo || !firstName || !lastName || !email || !invoiceEmail || loading}
                                            onClick={handleSaveBusinessInfo}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            )}
                            {isNewClient && showAddEquipmentTypeModal && (
                                <div className='clientNestedModalLayer'>
                                    <form
                                        className='clientEquipmentTypeDialog'
                                        onSubmit={(event) => {
                                            event.preventDefault()
                                            addCustomEquipmentType()
                                        }}
                                    >
                                        <h2 className='modalTitle'>Edit Equipment/Assets</h2>
                                        {equipmentTypePendingDelete && (
                                            <div className='clientEquipmentDeleteConfirm'>
                                                <h2 className='modalTitle'>Are you sure?</h2>
                                                <p>This will remove {equipmentTypePendingDelete.item.name} from the equipment list.</p>
                                                <div className='modalFooter'>
                                                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setEquipmentTypePendingDelete(null)}>Cancel</Button>
                                                    <Button variant="danger" size="md"
                                                        type='button'
                                                        className='modalDangerButton'
                                                        onClick={() => {
                                                            deleteClientEquipmentType(equipmentTypePendingDelete.item)
                                                            setEquipmentTypePendingDelete(null)
                                                        }}
                                                    >
                                                        Yes
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <div className='clientEquipmentTypeList'>
                                            <div className='clientEquipmentTypeListHeading'>
                                                <span>Type</span>
                                                <span>Action</span>
                                            </div>
                                            {clientEquipmentRows.map((item, index) => (
                                                <div className='clientEquipmentTypeListRow' key={`${item.sourceName}-${item.name}`}>
                                                    <span>{item.name}</span>
                                                    <div className='clientEquipmentTypeRowActions'>
                                                        <Button data-action-tone="edit" variant="secondary" size="md"
                                                            type='button'
                                                            className='clientEquipmentTypeEditAction'
                                                            onClick={() => {
                                                                setEquipmentTypeEditing(item)
                                                                setNewEquipmentTypeName(item.name)
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button data-action-tone="delete" variant="danger" size="md"  type='button' onClick={() => setEquipmentTypePendingDelete({ item, index })}>Delete</Button>
                                                    </div>
                                                </div>
                                            ))}
                                            {clientEquipmentOptions.length === 0 && <p className='clientEquipmentTypeEmpty'>No equipment types.</p>}
                                        </div>
                                        <div className='clientEquipmentTypeAddRow'>
                                            <Input
                                                value={newEquipmentTypeName}
                                                setValue={setNewEquipmentTypeName}
                                                label={equipmentTypeEditing ? 'Edit Type' : 'Add New'}
                                                placeholder='Enter a name'
                                            />
                                            <Button variant={equipmentTypeEditing ? 'secondary' : 'create'} size="md" type='submit' disabled={!newEquipmentTypeName.trim()}>{equipmentTypeEditing ? 'Update' : '+ Add'}</Button>
                                        </div>
                                        <div className='modalFooter'>
                                            <Button variant="secondary" size="md"
                                                type='button'
                                                className='secondaryButton'
                                                onClick={() => {
                                                    setNewEquipmentTypeName('')
                                                    setEquipmentTypeEditing(null)
                                                    setShowAddEquipmentTypeModal(false)
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='saveWithIcon' onClick={() => {
                                                setEquipmentTypeEditing(null)
                                                setNewEquipmentTypeName('')
                                                setShowAddEquipmentTypeModal(false)
                                            }}>
                                                Save
                                            </Button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            {showInvoiceEmailModal && (
                                <div className='clientNestedModalLayer'>
                                    <form
                                        className='clientInvoiceEmailDialog'
                                        role='dialog' aria-modal='true' aria-labelledby='clientInvoiceEmailTitle'
                                        onSubmit={(event) => {
                                            event.preventDefault()
                                            addInvoiceEmail()
                                        }}
                                    >
                                        <div className='clientInvoiceEmailDialogHeader'><h2 id='clientInvoiceEmailTitle'>Edit Additional Emails</h2></div>
                                        {invoiceEmailPendingDelete && (
                                            <div className='clientEquipmentDeleteConfirm'>
                                                <h2 className='modalTitle'>Are you sure?</h2>
                                                <p>This will remove {formatEmail(invoiceEmailPendingDelete.email)} from the invoice email list.</p>
                                                <div className='modalFooter'>
                                                    <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton' onClick={() => setInvoiceEmailPendingDelete(null)}>Cancel</Button>
                                                    <Button variant="danger" size="md"
                                                        type='button'
                                                        className='modalDangerButton'
                                                        onClick={() => {
                                                            deleteInvoiceEmail(invoiceEmailPendingDelete.index)
                                                            setInvoiceEmailPendingDelete(null)
                                                        }}
                                                    >
                                                        Yes
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        {invoiceEmailSaveSuccess && (
                                            <div className='clientEquipmentDeleteConfirm clientCompactSuccessMessage'>
                                                <h2 className='modalTitle'>Saved</h2>
                                                <p>Invoice emails updated successfully.</p>
                                                <div className='modalFooter'>
                                                    <Button variant="secondary" size="md"
                                                        type='button'
                                                        onClick={() => {
                                                            setInvoiceEmailSaveSuccess(false)
                                                            setInvoiceEmailEditingIndex(null)
                                                            setNewInvoiceEmail('')
                                                            setShowInvoiceEmailModal(false)
                                                        }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                        <div className='clientInvoiceEmailDialogBody'>
                                        <div className='clientInvoiceEmailDialogList'>
                                            <div className='clientInvoiceEmailDialogListHeading'>
                                                <span>Email</span>
                                                <span>Action</span>
                                            </div>
                                            {additionalInvoiceEmails.map((item, index) => (
                                                <div className='clientInvoiceEmailDialogListRow' key={`${item}-${index}`}>
                                                    <span className='clientInvoiceEmailDialogAddress'>{item}</span>
                                                    <div className='clientInvoiceEmailDialogRowActions'>
                                                        <Button data-action-tone="edit" variant="secondary" size="sm"
                                                            type='button'

                                                            onClick={() => {
                                                                setInvoiceEmailEditingIndex(index)
                                                                setNewInvoiceEmail(item)
                                                            }}
                                                        >
                                                            Edit
                                                        </Button>
                                                        <Button data-action-tone="delete" variant="danger" size="sm"  type='button' onClick={() => setInvoiceEmailPendingDelete({ email: item, index })}>Delete</Button>
                                                    </div>
                                                </div>
                                            ))}
                                            {additionalInvoiceEmails.length === 0 && <p className='clientInvoiceEmailDialogEmpty'>No additional invoice emails.</p>}
                                        </div>
                                        <div className='clientInvoiceEmailDialogAddRow'>
                                            <Input value={newInvoiceEmail} setValue={setNewInvoiceEmail} label={invoiceEmailEditingIndex !== null ? 'Edit email address' : 'Add email address'} placeholder='Email address' type='email' />
                                            <Button variant={invoiceEmailEditingIndex !== null ? 'secondary' : 'create'} size="md" type='submit' disabled={!newInvoiceEmail.trim()}>{invoiceEmailEditingIndex !== null ? 'Update' : '+ Add'}</Button>
                                        </div>
                                        </div>
                                        <div className='clientInvoiceEmailDialogFooter'>
                                            <Button variant="secondary" size="md"
                                                type='button'
                                                className='secondaryButton'
                                                onClick={() => {
                                                    setNewInvoiceEmail('')
                                                    setInvoiceEmailEditingIndex(null)
                                                    setShowInvoiceEmailModal(false)
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                            <Button data-action-tone="save" variant="soft" size="md"  type='button' className='saveWithIcon' onClick={() => setInvoiceEmailSaveSuccess(true)}>Save</Button>
                                        </div>
                                    </form>
                                </div>
                            )}
                            <div className='clientProfileTopRow'>
                                <Section title={'Business Details'} className='clientProfileCard clientBusinessDetailsCard' variant='card'>
                                    <div className='clientProfileFieldGrid'>
                                        <Dropdown label={'Business Type'} disabled={!editing} required options={businessTypeOptions} value={getDropdownValue(businessType, businessTypeOptions)} setValue={v => setBusinessType(v.value)} />
                                        <Input disabled={!editing} value={companyName} setValue={setCompanyName} placeholder={'Company Name'} label={'Company Name'} required />
                                        <div>
                                            <FieldLabel className='fieldLabel'>
                                                ABN {requiresAbn && <span className='requiredMark'>*</span>}
                                                {abnVerified && <span className='clientAbnVerified' aria-label='ABN verified'>✓ Verified</span>}
                                            </FieldLabel>
                                            <div className='clientAbnVerifyRow'>
                                                <UiInput
                                                    aria-label='ABN'
                                                    disabled={!editing}
                                                    value={abn}
                                                    onChange={(event) => {
                                                        setAbn(normalizeAbn(event.target.value))
                                                        setAbnVerified(false)
                                                    }}
                                                />
                                                {editing && (
                                                    <Button variant="secondary" size="md"
                                                        type='button'
                                                        className='clientAbnVerifyButton'
                                                        disabled={abnVerifying || normalizeAbn(abn).length !== 11}
                                                        onClick={handleAbnLookup}
                                                    >
                                                        {abnVerifying ? 'Verifying...' : 'Verify ABN'}
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                        <div className='clientProfileFieldSpanFull'>
                                            <FieldLabel className="fieldLabel">Address <span className='requiredMark'>*</span></FieldLabel>
                                            <UiInput as={AutoComplete}
                                                disabled={!editing}
                                                apiKey={googleApiKey}
                                                options={{
                                                    types: ["address"],
                                                    componentRestrictions: { country: "au" },
                                                }}
                                                defaultValue={address}
                                                onPlaceSelected={(place) => { setCoordinates([place.geometry.location.lat(), place.geometry.location.lng()]); setAddress(place.formatted_address); }}
                                                className='clientAutocompleteField'
                                            />
                                        </div>
                                    </div>

                                </Section>

                                <Section
                                    title={'Primary Contact'}
                                    className='clientProfileCard clientPrimaryContactCard'
                                    variant='card'
                                >
                                    <div className='clientProfileFieldGrid'>
                                        <Input disabled={!editing} value={firstName} setValue={setFirstName} placeholder={'First Name'} label={'First Name'} required />
                                        <Input disabled={!editing} value={lastName} setValue={setLastName} placeholder={'Last Name'} label={'Last Name'} required />
                                        <Input disabled={!editing} value={primaryContactNo} setValue={setPrimaryContactNo} placeholder={'+61'} label={'Contact Number'} required />
                                        <Input disabled={!editing} value={email} setValue={setEmail} placeholder={'Email'} label={'Email'} required />
                                    </div>
                                    <div className='clientAdditionalContactTableWrap'>
                                        {editing && (
                                            <div className='clientAdditionalContactActionRow'>
                                                {additionalContacts.length > 0 && (
                                                    <Button data-action-tone="edit" variant="secondary" size="md"
                                                        type='button'
                                                        className='clientAdditionalContactEditButton actionWithIcon'
                                                        onClick={openEditContactModal}
                                                    >
                                                        <EditIcon />Edit
                                                    </Button>
                                                )}
                                                <Button variant="create" size="sm" type='button' className='clientPrimaryContactAddButton clientNewContactButton' onClick={openAddContactModal}>
                                                    <PlusIcon />
                                                    Contact
                                                </Button>
                                            </div>
                                        )}
                                        <h4 className='clientAdditionalContactTableTitle'>Additional Contact</h4>
                                        <Table className='clientAdditionalContactTable'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell>First Name</TableHeaderCell>
                                                    <TableHeaderCell>Last Name</TableHeaderCell>
                                                    <TableHeaderCell>Email</TableHeaderCell>
                                                    <TableHeaderCell>Contact<br />Number</TableHeaderCell>
                                                    <TableHeaderCell>Primary<br />Contact</TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {additionalContacts.map((contact) => (
                                                    <TableRow key={contact.id || contact.email}>
                                                        <TableCell>{formatName(contact.firstName)}</TableCell>
                                                        <TableCell>{formatName(contact.lastName)}</TableCell>
                                                        <TableCell>{formatEmail(contact.email)}</TableCell>
                                                        <TableCell>{contact.contactNo || <span className='clientEmptyValue'>—</span>}</TableCell>
                                                        <TableCell>
                                                            <FieldLabel choice className='clientPrimaryContactChoice'>
                                                                <UiCheckbox
                                                                    type='radio'
                                                                    name='primary-client-contact'
                                                                    aria-label={formatName(`Set ${formatName(contact.firstName) || ''} ${formatName(contact.lastName) || ''} as primary contact`)}
                                                                    checked={primaryContactId === contact.id}
                                                                    disabled={!editing}
                                                                    onChange={() => selectPrimaryContact(contact)}
                                                                />
                                                                <span className={primaryContactId === contact.id ? 'clientPrimaryContactIndicator clientPrimaryContactIndicatorSelected' : 'clientPrimaryContactIndicator'} aria-hidden='true'>
                                                                    {primaryContactId === contact.id && <CheckIcon />}
                                                                </span>
                                                            </FieldLabel>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {additionalContacts.length === 0 && (
                                                    <TableEmpty  colSpan={5}>No additional contacts yet.</TableEmpty>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Section>
                            </div>

                            <Section
                                title={'Sites'}
                                className='width-100 clientProfileCard'
                                variant='card'
                            >
                                <div className='clientNewSiteActionRow'>
                                    <Button variant="create" size="sm" type='button' className='clientPrimaryContactAddButton clientNewSiteButton' onClick={openNewSitePanel}>
                                        <PlusIcon />
                                        Site
                                    </Button>
                                </div>
                                <div className='clientTableWrap'>
                                    <Table className='clientsDirectoryTable'>
                                        <TableHead>
                                            <TableRow>
                                                <TableHeaderCell {...sitesSort.getSortableHeaderProps('name')}>
                                                    Name
                                                </TableHeaderCell>
                                                <TableHeaderCell>
                                                    Address
                                                </TableHeaderCell>
                                                <TableHeaderCell>
                                                    Action
                                                </TableHeaderCell>
                                            </TableRow>
                                        </TableHead>
                                        <tbody>
                                            {sitesSort.sortedData.map(site => <TableRow key={site.id}>
                                                <TableCell className="hover">
                                                    <button
                                                        type='button'
                                                        className='clientDirectoryLinkButton'
                                                        onClick={() => openSitePanel(site)}
                                                    >
                                                        {site.name}
                                                    </button>
                                                    {site.archived && <span className='clientDirectoryStatusPill'>Archived</span>}
                                                </TableCell>
                                                <TableCell>{site.address}</TableCell>
                                                <TableCell className='clientDirectoryActionCell'>
                                                    <Button variant={isDraftSiteId(site.id) ? 'danger' : 'secondary'} size="icon"
                                                        type='button'
                                                        className={`recordActionIcon clientSiteArchiveIconButton${site.archived ? ' clientSiteUnarchiveIconButton' : ''}${isDraftSiteId(site.id) ? ' clientSiteDraftDeleteIconButton' : ''}`}
                                                        aria-label={isDraftSiteId(site.id) ? 'Delete Site' : (site.archived ? 'Unarchive Site' : 'Archive Site')}
                                                        title={isDraftSiteId(site.id) ? 'Delete Site' : (site.archived ? 'Unarchive Site' : 'Archive Site')}
                                                        onClick={(event) => {
                                                            event.stopPropagation()
                                                            if (isDraftSiteId(site.id)) {
                                                                setSites(currentSites => currentSites.filter(item => item.id !== site.id))
                                                            } else {
                                                                toggleSiteArchive(site.id)
                                                            }
                                                        }}
                                                    >
                                                        {isDraftSiteId(site.id) ? <TrashIcon /> : <ArchiveIcon />}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>)}
                                            {sitesSort.sortedData.length === 0 && (
                                                <TableEmpty  colSpan={3}>No sites yet.</TableEmpty>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>

                            </Section>

                            <Section title={'Accounts'} className='width-100 clientProfileCard' variant='card'
                                action={editing ? <Button data-action-tone="edit" variant="secondary" size="sm" type='button' onClick={() => {
                                    setInvoiceEmailEditingIndex(null)
                                    setNewInvoiceEmail('')
                                    setShowInvoiceEmailModal(true)
                                }}>Edit</Button> : null}>
                                <div className='clientAccountsEmailGrid'>
                                    <div className='clientInvoiceEmailFieldBlock'>
                                        <Input disabled={!editing} value={invoiceEmail} setValue={setInvoiceEmail} placeholder={'Email'} label={'Invoice Email'} required />
                                        <Dropdown disabled={!editing} label='Invoice Due dates' options={invoiceDueTermOptions}
                                            value={invoiceDueTermOptions.find(option => option.value === invoiceDueTerm) || { label: invoiceDueDays + ' days', value: invoiceDueTerm }}
                                            setValue={option => {
                                                if (!option) return
                                                setInvoiceDueTerm(option.value)
                                                setInvoiceDueDays(invoiceDueDaysForTerm(option.value))
                                            }} />
                                    </div>
                                    <div className='clientAccountsEmailListColumn'>

                                        <div className='clientAdditionalContactTableTitle clientAdditionalEmailTitle'>Additional Email</div>
                                        <Table emptyItems="additional emails" className='clientAdditionalContactTable clientAdditionalEmailTable'>
                                            <TableHead>
                                                <TableRow><TableHeaderCell>Email</TableHeaderCell></TableRow>
                                            </TableHead>
                                            <tbody>
                                                {additionalInvoiceEmails.map((item, index) => (
                                                    <TableRow key={`${item}-${index}`}><TableCell>{item}</TableCell></TableRow>
                                                ))}
                                                {additionalInvoiceEmails.length === 0 && (
                                                    <TableRow><TableCell className='clientEmptyValue'>No additional emails.</TableCell></TableRow>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Section>

                            <Section title={'Invoices'} className='width-100 clientProfileCard' variant='card'>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableHeaderCell>Invoice</TableHeaderCell>
                                            <TableHeaderCell>Site</TableHeaderCell>
                                            <TableHeaderCell>Issued</TableHeaderCell>
                                            <TableHeaderCell>Due</TableHeaderCell>
                                            <TableHeaderCell>Status</TableHeaderCell>
                                            <TableHeaderCell>Paid</TableHeaderCell>
                                            <TableHeaderCell>Total</TableHeaderCell>
                                        </TableRow>
                                    </TableHead>
                                    <tbody>
                                        {paginatedClientInvoices.map((invoice) => {
                                            const isExpanded = expandedClientInvoiceId === invoice.id
                                            return (
                                                <Fragment key={invoice.id}>
                                                    <TableRow flagged={invoice.status === 'Overdue'} className={isExpanded ? 'invoiceListRowSelected' : ''}>
                                                        <TableCell className='hover' onClick={() => setExpandedClientInvoiceId(isExpanded ? '' : invoice.id)}>
                                                            <span className='allocationDateLink'>{invoice.invoiceNumber}</span>
                                                        </TableCell>
                                                        <TableCell>{invoice.site?.name || '-'}</TableCell>
                                                        <TableCell>{invoice.date ? format(new Date(invoice.date), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                        <TableCell>{invoice.dueDate ? format(new Date(invoice.dueDate), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                        <TableCell>{getInvoiceStatusLabel(invoice)}</TableCell>
                                                        <TableCell>{invoice.paidDate ? format(new Date(invoice.paidDate), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                        <TableCell>${Number(invoice.total || 0).toFixed(2)}</TableCell>
                                                    </TableRow>
                                                    {isExpanded && (
                                                        <TableRow className='invoiceChildRow'>
                                                            <TableCell colSpan={7}>
                                                                <div className='invoiceHistoryBlock'>
                                                                    <h3 className='subPanelHeading'>Invoice History</h3>
                                                                    <Table emptyItems="activity entries">
                                                                        <TableHead>
                                                                            <TableRow>
                                                                                <TableHeaderCell>Date</TableHeaderCell>
                                                                                <TableHeaderCell>Action</TableHeaderCell>
                                                                                <TableHeaderCell>Status</TableHeaderCell>
                                                                                <TableHeaderCell>By</TableHeaderCell>
                                                                            </TableRow>
                                                                        </TableHead>
                                                                        <tbody>
                                                                            {(invoice.history || []).slice().reverse().map((row, index) => (
                                                                                <TableRow key={`${invoice.id}-history-${index}`}>
                                                                                    <TableCell>{row.date ? <>{format(new Date(row.date), 'EEE, d MMM yyyy')}<br />{formatTime(new Date(row.date))}</> : '-'}</TableCell>
                                                                                    <TableCell>{row.action || '-'}</TableCell>
                                                                                    <TableCell>{row.status || '-'}</TableCell>
                                                                                    <TableCell>{formatName(row.actorName) || '-'}</TableCell>
                                                                                </TableRow>
                                                                            ))}
                                                                        </tbody>
                                                                    </Table>
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )}
                                                </Fragment>
                                            )
                                        })}
                                        {clientInvoices.length === 0 && (
                                            <TableEmpty  colSpan={7}>No invoices yet.</TableEmpty>
                                        )}
                                    </tbody>
                                </Table>
                                {clientInvoices.length > 0 && (
                                    <div className='invoicePagination'>
                                        <Button variant="secondary" size="md" type='button' disabled={clientInvoicePage === 1} onClick={() => { setExpandedClientInvoiceId(''); setClientInvoicePage(page => Math.max(1, page - 1)) }}>Previous</Button>
                                        <span>Page {clientInvoicePage} of {clientInvoicePageCount}</span>
                                        <Button variant="secondary" size="md" type='button' disabled={clientInvoicePage === clientInvoicePageCount} onClick={() => { setExpandedClientInvoiceId(''); setClientInvoicePage(page => Math.min(clientInvoicePageCount, page + 1)) }}>Next</Button>
                                    </div>
                                )}
                            </Section>

                            {!isStaff && <Section title={'Rates'} className='width-100 clientProfileCard clientRatesCard' variant='card'>
                                {!!viewingClient?.id && <div className='clientRatesEquipmentHeader'>
                                    <p className='sectionHelperText'>Default billing rates for this client.</p>
                                    <Button variant="secondary" size="md" type='button' className='secondaryButton' disabled={loading || editing} title={editing ? 'Save the client rates before applying them to sites' : ''} onClick={applyClientRatesToAllSites}>Apply rates to all sites</Button>
                                </div>}
                                <RateTable
                                    labourRates={companyLabourRates}
                                    setLabourRates={setCompanyLabourRates}
                                    editing={editing}
                                />
                                <div className='clientRatesEquipmentBlock'>
                                    <div className='clientRatesEquipmentHeader'>
                                        <p className='clientRatesEquipmentTitle'>Equipment &amp; Asset</p>
                                        {editing && (
                                            <Button data-action-tone="edit" variant="secondary" size="md"
                                                type='button'
                                                className='clientAddEquipmentTypeButton actionWithIcon'
                                                onClick={() => setShowAddEquipmentTypeModal(true)}
                                            >
                                                <EditIcon />Equipment/Asset
                                            </Button>
                                        )}
                                    </div>
                                    <div className='clientEquipmentRatesTableWrap'>
                                        <Table emptyItems="equipment rates" className='clientEquipmentRatesTable'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell>Type</TableHeaderCell>
                                                    <TableHeaderCell>Rate</TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {clientEquipmentRows.map((row) => {
                                                    const item = row.name
                                                    const index = getEquipmentRateIndex(row)
                                                    return (
                                                    <TableRow key={`${row.custom ? 'custom' : 'builtin'}-${row.sourceName}`}>
                                                        <TableCell>{item}</TableCell>
                                                        <TableCell>
                                                            <div className={`clientRateCurrencyInput clientEquipmentCurrencyInput ${editing ? '' : 'clientRateCurrencyInputDisabled'}`}>
                                                                <span aria-hidden="true">$</span>
                                                                <UiInput
                                                                    type="text"
                                                                    inputMode="decimal"
                                                                    aria-label={`${item} rate`}
                                                                    value={rateDayEquipmentCompany[index] || ''} onChange={e => {
                                                                        const newRates = [...rateDayEquipmentCompany];
                                                                        newRates[index] = e.target.value;
                                                                        setRateDayEquipmentCompany(newRates);
                                                                    }}
                                                                    disabled={!editing}
                                                                    placeholder="0"
                                                                />
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                    )
                                                })}
                                            </tbody>
                                        </Table>
                                    </div>
                                </div>
                            </Section>}
                            {!isNewClient && viewingClient?.id && <ClientActivityLog orgId={activeOrg.id} clientId={viewingClient.id} clientNumber={viewingClient.clientNumber} />}
                        </div>
                    </> : <>
                        {editingSite && !isNewSite && (
                            <div className='clientSiteStickyTopActions'>


                                <Button data-action-tone="save" variant="soft" size="md"  className=' saveWithIcon' disabled={loading || !siteName.trim() || !siteAddress.trim() || !managerFirstName.trim() || !managerLastName.trim() || !managerEmail.trim() || !managerContactNo.trim() || (sendTimesheetsByEmail && (!timesheetEmailFrequency || !timesheetEmailRecipients.trim()))} onClick={handleSaveSiteInfo}>Save</Button>
                                <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={closeSitePanel}>Cancel</Button>

                                <Button data-action-tone="archive" variant="secondary" size="md"
                                    type='button'
                                    className='clientSiteArchiveButton'
                                    onClick={() => setSiteArchived(value => !value)}
                                >
                                    {siteArchived ? 'Unarchive Site' : 'Archive Site'}
                                </Button>
</div>
                        )}
                        {!editingSite && <div className='clientProfileShell'>
                            <div className='clientProfileToolbar'>
                                <div className='recordHeaderActions'>
                                    <Button data-action-tone="edit" variant="secondary" size="md"  className='clientTopActionButton actionWithIcon' onClick={() => setEditingSite(true)}><EditIcon />Edit</Button>
                                    <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton clientTopActionButton clientSiteCloseButton' onClick={closeSitePanel}>Cancel</Button>
                                </div>
                            </div>
                        </div>}
                        <div className={`clientSiteSectionStack ${isNewSite ? 'clientNewSiteForm' : ''}`}>
                            {isNewSite && (
                                <div className='clientNewSiteModalHeader'>
                                    <div>
                                        <h2>New Site</h2>
                                        <p>Add the site, manager, timesheet, document, and rate details.</p>
                                    </div>
                                    <div className='clientNewSiteHeaderActions'>
                                        <Button data-action-tone="cancel" variant="secondary" size="md"  type='button' className='secondaryButton clientTopActionButton' onClick={closeSitePanel}>Cancel</Button>
                                        <Button data-action-tone="save" variant="soft" size="md"
                                            type='button'
                                            className='clientTopActionButton saveWithIcon'
                                            disabled={loading || !siteName.trim() || !siteAddress.trim() || !managerFirstName.trim() || !managerLastName.trim() || !managerEmail.trim() || !managerContactNo.trim() || (sendTimesheetsByEmail && (!timesheetEmailFrequency || !timesheetEmailRecipients.trim()))}
                                            onClick={handleSaveSiteInfo}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            )}
                            <div className='clientSiteTopRow'>
                                <Section title={'Site'} className='clientProfileCard' variant='card'>
                                    <div className='clientSiteFieldGrid clientSiteFieldGridTwo'>
                                        <Input disabled={!editingSite} value={siteName} setValue={setSiteName} placeholder={'Name'} label={'Name'} required />
                                        <div className='clientSiteFieldSpanFull'>
                                            <FieldLabel className="fieldLabel">Address <span className='requiredMark'>*</span></FieldLabel>
                                            <UiInput as={AutoComplete}
                                                apiKey={googleApiKey}
                                                disabled={!editingSite}
                                                options={{
                                                    types: ["address"],
                                                    componentRestrictions: { country: "au" },
                                                }}
                                                defaultValue={siteAddress}
                                                onPlaceSelected={(place) => { setSiteCoordinates([place.geometry.location.lat(), place.geometry.location.lng()]); setSiteAddress(place.formatted_address); }}
                                                className='clientAutocompleteField'
                                            />
                                        </div>
                                    </div>
                                </Section>


                                <Section
                                    title={'Site Manager'}
                                    className='clientProfileCard'
                                    variant='card'
                                    action={editingSite ? <Button variant="secondary" size="md" className='clientInlineHeaderButton clientSiteManagerCopyButton' type='button' onClick={copyPrimaryContactToSite}><CopyIcon />Primary Contact</Button> : null}
                                >
                                    <div className='clientSiteFieldGrid clientSiteFieldGridTwo'>
                                        <Input disabled={!editingSite} value={managerFirstName} setValue={setManagerFirstName} placeholder={'First Name'} label={'First Name'} required />
                                        <Input disabled={!editingSite} value={managerLastName} setValue={setManagerLastName} placeholder={'Last Name'} label={'Last Name'} required />
                                        <Input disabled={!editingSite} value={managerEmail} setValue={setManagerEmail} placeholder={'Email'} label={'Email'} required />
                                        <Input disabled={!editingSite} value={managerContactNo} setValue={setManagerContactNo} placeholder={'+61'} label={'Contact Number'} required />
                                    </div>
                                </Section>
                            </div>

                            <Section title='Site Management' className='width-100 clientProfileCard clientSiteManagementContainer' variant='card'>
                            <div className='clientSiteManagementTopColumns'>
                            <Section title='Site Portal' className='width-100 clientProfileCard clientSitePortalCard' variant='card' action={<Button variant="secondary" size="md" type='button' className='clientPrimaryContactAddButton clientSiteAddDocumentButton clientPortalAccessButton' disabled={editingSite || !viewingClient?.id || !viewingSite?.id || isDraftSiteId(viewingSite.id)} title={editingSite || !viewingClient?.id || !viewingSite?.id || isDraftSiteId(viewingSite.id) ? 'Save the site first' : 'Manage who can use Site Hub for this site'} onClick={() => setManagingPortalAccess(true)}>-/+ Access</Button>}>
                                <div className='clientTableWrap'><Table className='clientsDirectoryTable clientPortalContactsTable'><TableHead><TableRow><TableHeaderCell>First Name</TableHeaderCell><TableHeaderCell>Last Name</TableHeaderCell><TableHeaderCell>Mobile</TableHeaderCell><TableHeaderCell>Email</TableHeaderCell></TableRow></TableHead><tbody>
                                    {hasSiteManagerContact && <TableRow>{['firstName', 'lastName', 'mobile', 'email'].map(field => <TableCell key={field}>{siteManagerPortalContact[field] || '-'}</TableCell>)}</TableRow>}
                                    {clientAccessContacts.filter(contact => !hasSiteManagerContact || !isSiteManagerContact(contact)).map((contact, index) => <TableRow key={contact.id || index}>{['firstName', 'lastName', 'mobile', 'email'].map(field => <TableCell key={field}>{contact[field] || '-'}</TableCell>)}</TableRow>)}
                                    {!hasSiteManagerContact && !clientAccessContacts.length && <TableEmpty  colSpan={4}>No client access contacts yet.</TableEmpty>}
                                </tbody></Table></div>
                            </Section>
                            <Section title={'Timesheet'} className='width-100 clientProfileCard' variant='card'>
                                <div className='clientCheckboxRow'>
                                    <Checkbox disabled={!editingSite} value={sendTimesheetsByEmail} setValue={(value) => {
                                        setSendTimesheetsByEmail(value)
                                        if (!value) {
                                            setTimesheetEmailFrequency('')
                                            setTimesheetEmailRecipients('')
                                        }
                                    }} label={'Send timesheets by email'} />
                                </div>
                                {sendTimesheetsByEmail && (
                                    <div className='clientSiteFieldGrid clientSiteTimesheetFields'>
                                        <Dropdown
                                            disabled={!editingSite}
                                            label={'Timesheet Email Frequency'}
                                            required
                                            options={timesheetEmailFrequencyOptions}
                                            value={getDropdownValue(timesheetEmailFrequency, timesheetEmailFrequencyOptions)}
                                            setValue={v => setTimesheetEmailFrequency(v.value)}
                                        />
                                        <EmailRecipientPills key={String(viewingSite?.id) + ':' + editingSite} disabled={!editingSite} value={timesheetEmailRecipients} onChange={setTimesheetEmailRecipients} />

                                    </div>
                                )}
                            </Section>
                            </div>
                            <div className='clientSiteMediaRow'>
                                <div className='clientSiteDocumentColumn'>
                                <Section
                                    title={'Site Documents'}
                                    className='clientProfileCard clientSiteDocumentsCard'
                                    variant='card'
                                    action={!addPicture ? <Button variant="create" size="md" type='button' className='clientPrimaryContactAddButton clientSiteAddDocumentButton' onClick={() => { setAddPicture(true); setAddPictureIndex(-1); setPicture(''); setPictureName(''); setPictureDate(''); setPictureNote(''); setPictureIncludeInNewAllocation(false) }}><PlusIcon />Document</Button> : null}
                                >

                                    <div className='clientTableWrap clientSiteDocumentsTableWrap'>
                                        <Table className='clientsDirectoryTable clientDataTableFixed clientSiteDocumentsTable'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell>Name</TableHeaderCell>
                                                    <TableHeaderCell className='clientColDocDate'>Date</TableHeaderCell>
                                                    <TableHeaderCell>Attached to Allocation</TableHeaderCell>
                                                    <TableHeaderCell>Action</TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {pictures.map((row, index) => {
                                                    const documentUrl = typeof row.picture === 'string' ? row.picture : row.picture?.url
                                                    return (
                                                        <TableRow key={row.id || `service-${index}`}>
                                                            <TableCell className='clientCellBreak'>
                                                                {getDocumentDisplayName(row)}
                                                            </TableCell>
                                                            <TableCell>{row.date ? format(new Date(row.date), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                            <TableCell>{row.includeInNewAllocation ? 'Yes' : 'No'}</TableCell>
                                                            <TableCell>
                                                                <div className='clientSiteDocumentActions'>
                                                                    <Button data-action-tone="edit" variant="secondary" size="sm"
                                                                        type='button'
                                                                        onClick={() => isWebSwmsForm(row)
                                                                            ? openSiteFormDocument(row, index, false)
                                                                            : openEditSiteDocument(row, index)}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                    {isWebSwmsForm(row) ? (
                                                                        <Button variant="secondary" size="sm"
                                                                            type='button'
                                                                            onClick={() => openSiteFormDocument(row, index, true)}
                                                                        >
                                                                            View
                                                                        </Button>
                                                                    ) : documentUrl ? (
                                                                        <a href={documentUrl} target='_blank' rel='noreferrer'>View</a>
                                                                    ) : (
                                                                        <span>-</span>
                                                                    )}
                                                                    {documentUrl && (
                                                                        <a onClick={showLinkDownloadProgress} href={documentUrl} target='_blank' rel='noreferrer' download={row.name || 'site-document.pdf'}>Download</a>
                                                                    )}
                                                                </div>
                                                            </TableCell>
                                                        </TableRow>
                                                    )
                                                })}
                                                {pictures.length === 0 && (
                                                    <TableEmpty  colSpan={4}>No documents yet.</TableEmpty>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                </Section>

                                </div>

                                <Section title={'Site Images'} className='clientProfileCard' variant='card'>
                                    <div className='clientTableWrap clientSiteImagesTableWrap'>
                                        <Table className='clientsDirectoryTable clientDataTableFixed clientSiteImagesTable'>
                                            <TableHead>
                                                <TableRow>
                                                    <TableHeaderCell className='clientColWorker'>Worker</TableHeaderCell>
                                                    <TableHeaderCell className='clientColDocDate'>Date</TableHeaderCell>
                                                    <TableHeaderCell className='clientColWorkerImage'>Action</TableHeaderCell>
                                                </TableRow>
                                            </TableHead>
                                            <tbody>
                                                {workerUploads.slice((currentSiteImagePage - 1) * 5, currentSiteImagePage * 5).map((row, index) => (
                                                    <TableRow key={`worker-upload-${index}`}>
                                                        <TableCell className='clientCellBreak'>
                                                            {getWorkerName(row.workerId)}
                                                        </TableCell>
                                                        <TableCell>{row.date ? format(new Date(row.date), 'EEE, d MMM yyyy') : '-'}</TableCell>
                                                        <TableCell>
                                                            <Button variant="secondary" size="sm" type='button' className='clientInlineHeaderButton' onClick={() => setSiteImagePreview({ file: row.picture, label: `Site upload ${(currentSiteImagePage - 1) * 5 + index + 1}` })}>View</Button>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                                {workerUploads.length === 0 && (
                                                    <TableEmpty  colSpan={3}>No worker uploads yet.</TableEmpty>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    {workerUploads.length > 5 && <div className='clientSiteImagePagination'><Button variant="secondary" size="md" type='button' className='secondaryButton' disabled={currentSiteImagePage === 1} onClick={() => setSiteImagePage(currentSiteImagePage - 1)}>Previous</Button><span>Page {currentSiteImagePage} of {siteImagePageCount}</span><Button variant="secondary" size="md" type='button' className='secondaryButton' disabled={currentSiteImagePage === siteImagePageCount} onClick={() => setSiteImagePage(currentSiteImagePage + 1)}>Next</Button></div>}
                                </Section>
                            </div>


                            </Section>

                            {!isStaff && <Section title={'Rates'} className='width-100 clientProfileCard clientRatesCard' variant='card'>
                                <RateTable
                                    labourRates={siteLabourRates}
                                    setLabourRates={setSiteLabourRates}
                                    editing={editingSite}
                                />
                                <div className='clientRatesEquipmentBlock'>
                                    <p className='clientRatesEquipmentTitle'>Equipment &amp; Asset</p>
                                    <div className='clientSiteEquipmentGrid'>
                                        {equipmentOptions.map((item, index) => (
                                            <div key={index} className='clientEquipmentRateRow'>
                                                <div className='rateRowLabel'>{item}</div>
                                                <div className={`clientRateCurrencyInput clientEquipmentCurrencyInput ${editingSite ? '' : 'clientRateCurrencyInputDisabled'}`}>
                                                    <span aria-hidden="true">$</span>
                                                    <UiInput
                                                        type="text"
                                                        inputMode="decimal"
                                                        aria-label={`${item} rate`}
                                                        value={rateDayEquipment[index] || ''} onChange={e => {
                                                            const newRates = [...rateDayEquipment];
                                                            newRates[index] = e.target.value;
                                                            setRateDayEquipment(newRates);
                                                        }}
                                                        disabled={!editingSite}
                                                        placeholder="0"
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Section>}
                            {!isStaff && <Section title={'Rates to Workers'} className='width-100 clientProfileCard clientWorkerRatesCard' variant='card'>
                                <FieldLabel choice className='clientWorkerRatesToggle'>
                                    <UiCheckbox
                                        type='checkbox'
                                        checked={applyWorkerRatesToSite}
                                        disabled={!editingSite}
                                        onChange={event => setApplyWorkerRatesToSite(event.target.checked)}
                                    />
                                    <span>
                                        <strong>Apply custom worker rates at this site</strong>
                                        <small>Configured employee rates override employee profile rates for timesheets completed at this site. Blank rates continue using the employee profile. Contractor rates continue using each contractor's profile.</small>
                                    </span>
                                </FieldLabel>
                                {applyWorkerRatesToSite && <div className='clientWorkerRatesTables'>
                                    <div>
                                        <h3>Employee Rates</h3>
                                        <EmployeeRatesTable rates={siteEmployeeRates} setRates={setSiteEmployeeRates} editing={editingSite} />
                                    </div>
                                </div>}
                            </Section>}
                        </div>


                    </>}
                </> : <>
                    <Section title={'Clients'} className='width-100 clientProfileCard' variant='card'>
                        <div className='listToolbar clientsDirectoryToolbar'>
                            <div className='listToolbarSearch'>
                                <div className="dashboardSearchField uiSearchContainer">

                                    <SearchInput aria-label='Search' type='text' value={clientSearch} placeholder='Search' onChange={(e) => setClientSearch(e.target.value)} />
                                </div>
                            </div>
                            <Button variant="create" size="md" className='clientListAddButton' onClick={() => { createdClientIdRef.current = ''; savedDraftSiteIdsRef.current = new Set(); setSites([]); setViewingSite(false); setViewingClient({}); setEditing(true) }}>
                                <PlusIcon />
                                Client
                            </Button>
                        </div>
                        <div className='clientTableWrap'>
                            <Table className='clientsDirectoryTable clientsDirectoryListTable'>
                                <TableHead>
                                    <TableRow>
                                        <TableHeaderCell {...clientsSort.getSortableHeaderProps('name')}>
                                            Name
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Address
                                        </TableHeaderCell>
                                        <TableHeaderCell>
                                            Action
                                        </TableHeaderCell>
                                    </TableRow>
                                </TableHead>
                                <tbody>
                                    {visibleClients.length === 0 ? (
                                        <TableEmpty  colSpan={3}>No clients yet.</TableEmpty>
                                    ) : visibleClients.map(client => <TableRow key={client.id}>
                                        <TableCell className="hover">
                                            <button
                                                type='button'
                                                className='clientDirectoryLinkButton'
                                                onClick={() => openClientPanel(client)}
                                            >
                                                {formatCompanyName(client.name)}{client.clientNumber && <div className='uiTableSecondary'>{client.clientNumber}</div>}
                                            </button>
                                        </TableCell>
                                        <TableCell>{client.address || <span className='clientEmptyValue'>—</span>}</TableCell>
                                        <TableCell className='clientDirectoryActionCell'>
                                            <Button variant="secondary" size="sm" className='clientDirectoryActionButton'><ReceiptIcon />Create</Button>
                                            <Button variant="secondary" size="sm" className='clientDirectoryActionButton'><MailIcon />Send</Button>
                                        </TableCell>
                                    </TableRow>)}
                                </tbody>
                            </Table>
                        </div>
                    </Section>
                </>}
            </PageContent>
        </div>
    </div >)
}

export default ClientProfile
